// ══════════════════════════════════════════════════════
// Strategy Lab — Analyze Tab
// Run Analyzer on WF/Optimizer runs, render results inline
// ══════════════════════════════════════════════════════

import { useState, useEffect, useMemo, useCallback } from "react";
import { theme, fonts } from "../../theme";
import { Card, CardHeader, Pill, Button, SectionTitle, LoadingState } from "../../components/ui";
import { toast } from "../../components/shared";
import { postApi, getApi, fDateShort } from "./constants";
import { API_BASE } from "../../config";

// ─── Section definitions per run type ─────────────────
const OPT_SECTIONS = [
  { id: "summary", label: "Zusammenfassung", default: true },
  { id: "grades", label: "Grade-Verteilung", default: true },
  { id: "disqualifications", label: "Hard-Constraint Violations", default: true },
  { id: "top", label: "Top Trials", default: true },
  { id: "clusters", label: "Cluster-Analyse", default: true },
  { id: "importance", label: "Parameter-Importance", default: true },
  { id: "sensitivity", label: "Parameter-Sensitivität", default: true },
  { id: "stability", label: "Stabilitäts-Zonen", default: false },
  { id: "negative_combos", label: "Negativ-Kombinationen", default: true },
  { id: "score_breakdown", label: "Score-Breakdown", default: false },
  { id: "recommendations", label: "Empfehlungen", default: true },
];

const WF_SECTIONS = [
  { id: "summary", label: "Zusammenfassung", default: true },
  { id: "segmentation", label: "Win/Loss-Segmentierung", default: true },
  { id: "regime", label: "Regime (Trend/Range)", default: true },
  { id: "anomalies", label: "Trend-Loss-Anomalien", default: true },
  { id: "drift", label: "Parameter-Drift", default: true },
  { id: "sensitivity", label: "Parameter-Sensitivität", default: true },
  { id: "importance", label: "Parameter-Importance", default: true },
  { id: "windows", label: "Alle Windows (Detail)", default: false },
  { id: "recommendations", label: "Empfehlungen", default: true },
];

// ─── Markdown Renderer (lightweight, theme-matched) ──
// Parses: h1/h2/h3, paragraphs, tables, bullet lists, numbered lists,
// code (inline and block), bold, italic, blockquotes, horizontal rules,
// HTML comments (ignored).

function renderInline(text) {
  // Handle inline code first (to protect from other replacements)
  const parts = [];
  let remaining = text;
  let key = 0;
  const codeRegex = /`([^`]+)`/;

  while (true) {
    const m = remaining.match(codeRegex);
    if (!m) {
      parts.push(formatBoldItalic(remaining, key++));
      break;
    }
    if (m.index > 0) {
      parts.push(formatBoldItalic(remaining.slice(0, m.index), key++));
    }
    parts.push(
      <code key={`code-${key++}`} style={{
        fontFamily: fonts.mono,
        fontSize: "0.88em",
        padding: "2px 6px",
        borderRadius: 4,
        background: theme.tint,
        color: theme.text,
        border: `1px solid ${theme.border}`,
      }}>{m[1]}</code>
    );
    remaining = remaining.slice(m.index + m[0].length);
  }
  return parts;
}

function formatBoldItalic(text, baseKey) {
  // **bold** and *italic* (simple non-nested)
  const out = [];
  let rest = text;
  let k = 0;
  // Bold
  const boldR = /\*\*([^*]+)\*\*/;
  while (true) {
    const m = rest.match(boldR);
    if (!m) { out.push(<span key={`${baseKey}-t${k++}`}>{rest}</span>); break; }
    if (m.index > 0) out.push(<span key={`${baseKey}-t${k++}`}>{rest.slice(0, m.index)}</span>);
    out.push(<strong key={`${baseKey}-b${k++}`} style={{fontWeight:700,color:theme.text}}>{m[1]}</strong>);
    rest = rest.slice(m.index + m[0].length);
  }
  return <span key={`bi-${baseKey}`}>{out}</span>;
}

// ─── Chart Components (inline SVG, theme-consistent) ──────────────

function GradeDonut({ headers, rows }) {
  // Expected columns: Grade | Count | % | Ø PnL | Ø DD | Ø Trades
  // We only need Grade and Count
  const gradeIdx = headers.findIndex(h => /grade/i.test(h));
  const countIdx = headers.findIndex(h => /count/i.test(h));
  const pnlIdx = headers.findIndex(h => /pnl/i.test(h));
  if (gradeIdx < 0 || countIdx < 0) return null;

  const data = rows.map(r => ({
    grade: r[gradeIdx],
    count: parseInt(r[countIdx]) || 0,
    pnl: pnlIdx >= 0 ? r[pnlIdx] : "",
  })).filter(d => d.count > 0);

  const total = data.reduce((s, d) => s + d.count, 0);
  if (total === 0) return null;

  const colors = {
    A: "#10b981", B: "#3b82f6", C: "#f59e0b", D: "#f97316", F: "#ef4444", "?": "#94a3b8",
  };

  const cx = 100, cy = 100, r = 70, rInner = 45;
  let cumAngle = -Math.PI / 2;
  const segments = data.map(d => {
    const frac = d.count / total;
    const angle = frac * 2 * Math.PI;
    const x1 = cx + r * Math.cos(cumAngle);
    const y1 = cy + r * Math.sin(cumAngle);
    const x2 = cx + r * Math.cos(cumAngle + angle);
    const y2 = cy + r * Math.sin(cumAngle + angle);
    const x3 = cx + rInner * Math.cos(cumAngle + angle);
    const y3 = cy + rInner * Math.sin(cumAngle + angle);
    const x4 = cx + rInner * Math.cos(cumAngle);
    const y4 = cy + rInner * Math.sin(cumAngle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const path = `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4} Z`;
    cumAngle += angle;
    return { ...d, path, color: colors[d.grade] || "#94a3b8", pct: frac * 100 };
  });

  return (
    <div style={{ display: "flex", gap: 30, alignItems: "center", margin: "14px 0", padding: "16px",
                  background: theme.white, border: `1px solid ${theme.divider}`, borderRadius: 10 }}>
      <svg width="200" height="200" viewBox="0 0 200 200" style={{ flexShrink: 0 }}>
        {segments.map((s, i) => (
          <path key={i} d={s.path} fill={s.color} stroke={theme.white} strokeWidth="2"/>
        ))}
        <text x={cx} y={cy - 4} textAnchor="middle" style={{ fontSize: 28, fontWeight: 800, fill: theme.text, fontFamily: fonts.mono }}>
          {total}
        </text>
        <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 10, fill: theme.text3, textTransform: "uppercase", letterSpacing: 1 }}>
          Trials
        </text>
      </svg>
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))", gap: 10 }}>
        {segments.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                                background: theme.tint, borderRadius: 8, borderLeft: `4px solid ${s.color}` }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.text3, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Grade {s.grade}
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: s.color, fontFamily: fonts.mono }}>
                {s.count} <span style={{ fontSize: 11, color: theme.text4, fontWeight: 500 }}>({s.pct.toFixed(1)}%)</span>
              </div>
              {s.pnl && <div style={{ fontSize: 10, color: theme.text4, fontFamily: fonts.mono }}>Ø {s.pnl}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// v6-SMART-BUILDER: Multi-choice editor for list fields (e.g. adx_threshold_choices)
// Shows current values as toggle-chips, plus input to add new values
function SmartMultiChoice({ currentValue, newValue, numeric, onChange, theme, fonts }) {
  // newValue is an array of currently-active values. If null/undefined, default to currentValue.
  const active = Array.isArray(newValue) ? newValue : (Array.isArray(currentValue) ? [...currentValue] : []);
  const [addVal, setAddVal] = React.useState("");

  const toggle = (v) => {
    if (active.includes(v)) {
      onChange(active.filter(x => x !== v));
    } else {
      const next = [...active, v];
      // Sort numerically if numeric, otherwise alphabetically
      next.sort(numeric ? (a, b) => a - b : undefined);
      onChange(next);
    }
  };

  const addCustom = () => {
    if (!addVal.trim()) return;
    const val = numeric ? Number(addVal) : addVal.trim();
    if (numeric && (isNaN(val) || !isFinite(val))) return;
    if (active.includes(val)) return;
    const next = [...active, val];
    next.sort(numeric ? (a, b) => a - b : undefined);
    onChange(next);
    setAddVal("");
  };

  // Union of original + active so user can toggle back on
  const all = Array.from(new Set([...(currentValue || []), ...active]));
  all.sort(numeric ? (a, b) => a - b : undefined);

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 6 }}>
        {all.map(v => {
          const isActive = active.includes(v);
          return (
            <button key={String(v)} onClick={() => toggle(v)}
              style={{
                padding: "3px 8px", fontSize: 10.5, fontFamily: fonts.mono,
                borderRadius: 4, cursor: "pointer",
                background: isActive ? theme.greenBright : theme.white,
                color: isActive ? "#fff" : theme.text3,
                border: `1px solid ${isActive ? theme.greenBright : theme.border}`,
              }}>
              {isActive ? "✓ " : ""}{String(v)}
            </button>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
        <input
          type={numeric ? "number" : "text"}
          value={addVal}
          onChange={e => setAddVal(e.target.value)}
          onKeyDown={e => e.key === "Enter" && addCustom()}
          placeholder={numeric ? "Neuer Wert..." : "Neuer Text..."}
          style={{ padding: "3px 6px", fontSize: 10, width: 100,
            border: `1px solid ${theme.border}`, borderRadius: 3, fontFamily: fonts.mono }}
        />
        <button onClick={addCustom}
          style={{ padding: "3px 8px", fontSize: 10, borderRadius: 3,
            background: theme.blue, color: "#fff", border: "none", cursor: "pointer" }}>
          + Hinzufügen
        </button>
      </div>
    </div>
  );
}

function SensitivityBars({ paramName, headers, rows }) {
  // Expected columns: Wert | N | Ø Score | Ø PnL | Ø DD
  const valIdx = 0;
  const nIdx = headers.findIndex(h => /^\s*n\s*$/i.test(h));
  const scoreIdx = headers.findIndex(h => /score/i.test(h));
  const pnlIdx = headers.findIndex(h => /pnl/i.test(h));
  if (scoreIdx < 0) return null;

  const data = rows.map(r => ({
    value: r[valIdx],
    n: nIdx >= 0 ? parseInt(r[nIdx]) || 0 : 0,
    score: parseFloat(r[scoreIdx]) || 0,
    pnl: pnlIdx >= 0 ? r[pnlIdx] : "",
  }));
  if (data.length === 0) return null;

  const maxScore = Math.max(...data.map(d => d.score), 0.1);
  const maxN = Math.max(...data.map(d => d.n), 1);
  const bestIdx = data.reduce((best, d, i) => d.score > data[best].score ? i : best, 0);

  const barHeight = 22;
  const rowHeight = barHeight + 10;
  const labelWidth = 60;
  const nWidth = 50;
  const W = 640;
  const barStart = labelWidth + 8;
  const barEnd = W - nWidth - 90;
  const barWidth = barEnd - barStart;

  return (
    <div style={{ margin: "14px 0", padding: "14px 16px",
                  background: theme.white, border: `1px solid ${theme.divider}`, borderRadius: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.text, fontFamily: fonts.mono, marginBottom: 8 }}>
        {paramName}
        <span style={{ marginLeft: 12, fontSize: 10, color: theme.text4, fontWeight: 500 }}>
          Bester Ø Score: {data[bestIdx].score.toFixed(3)} @ {data[bestIdx].value}
        </span>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${rowHeight * data.length + 10}`} style={{ display: "block" }}>
        {data.map((d, i) => {
          const y = i * rowHeight + 5;
          const bw = (d.score / maxScore) * barWidth;
          const isBest = i === bestIdx;
          const color = isBest ? "#10b981" : d.score >= 0.8 ? "#3b82f6" : d.score >= 0.5 ? "#64748b" : "#cbd5e1";
          const nBarWidth = (d.n / maxN) * 50;

          return (
            <g key={i}>
              {/* Value label */}
              <text x={labelWidth} y={y + barHeight * 0.68} textAnchor="end"
                    style={{ fontSize: 12, fontFamily: fonts.mono, fill: isBest ? theme.greenText : theme.text2, fontWeight: isBest ? 700 : 500 }}>
                {d.value}
              </text>
              {/* Score bar background */}
              <rect x={barStart} y={y} width={barWidth} height={barHeight} fill={theme.tint} rx="3"/>
              {/* Score bar fill */}
              <rect x={barStart} y={y} width={Math.max(bw, 2)} height={barHeight} fill={color} rx="3"/>
              {/* Score value on bar */}
              <text x={barStart + bw + 6} y={y + barHeight * 0.68}
                    style={{ fontSize: 11, fontFamily: fonts.mono, fill: theme.text, fontWeight: 600 }}>
                {d.score.toFixed(3)}
              </text>
              {/* N count mini-bar */}
              <rect x={barEnd + 10} y={y + barHeight * 0.25} width={nBarWidth} height={barHeight * 0.5} fill={theme.blue} opacity="0.3" rx="2"/>
              <text x={barEnd + 65} y={y + barHeight * 0.68}
                    style={{ fontSize: 10, fontFamily: fonts.mono, fill: theme.text3 }}>
                n={d.n}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ViolationsBars({ headers, rows }) {
  // Expected columns: Regel | Verletzungen | % der disqualifizierten | Beispiel
  const ruleIdx = 0;
  const countIdx = headers.findIndex(h => /verletzung|count/i.test(h));
  const pctIdx = headers.findIndex(h => /%/.test(h));
  if (countIdx < 0) return null;

  const data = rows.map(r => ({
    rule: r[ruleIdx].replace(/[`*]/g, "").replace(/\s*\([^)]+\)\s*$/, ""),
    count: parseInt(r[countIdx]) || 0,
    pct: pctIdx >= 0 ? parseFloat(r[pctIdx]) || 0 : 0,
  })).filter(d => d.count > 0);

  if (data.length === 0) return null;

  const maxCount = Math.max(...data.map(d => d.count));
  const colors = ["#ef4444", "#f97316", "#f59e0b", "#a855f7"];

  return (
    <div style={{ margin: "14px 0", padding: "14px 18px",
                  background: theme.white, border: `1px solid ${theme.divider}`, borderRadius: 10 }}>
      {data.map((d, i) => {
        const color = colors[i % colors.length];
        const barPct = (d.count / maxCount) * 100;
        return (
          <div key={i} style={{ marginBottom: i === data.length - 1 ? 0 : 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: theme.text }}>{d.rule}</span>
              <span style={{ fontSize: 12, fontFamily: fonts.mono, color: theme.text2 }}>
                <b style={{ color: color, fontSize: 14 }}>{d.count}</b>
                <span style={{ color: theme.text4, marginLeft: 6 }}>({d.pct.toFixed(1)}%)</span>
              </span>
            </div>
            <div style={{ height: 8, background: theme.tint, borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${barPct}%`, background: color, borderRadius: 4, transition: "width 0.4s ease" }}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StabilityHeatmap({ headers, rows }) {
  // First column is row label (e.g. "sl_atr_mult \ adx_threshold"), rest are x-axis values
  // Cells contain "— " or "○ 0.52" or "● 1.29" (circle symbol + score)
  if (headers.length < 3 || rows.length === 0) return null;

  const parseCellScore = (cell) => {
    const clean = cell.replace(/[○●]/g, "").trim();
    if (clean === "—" || clean === "" || clean === "-") return null;
    const n = parseFloat(clean);
    return isNaN(n) ? null : n;
  };

  const xLabels = headers.slice(1);
  const yLabels = rows.map(r => r[0]);
  const matrix = rows.map(r => r.slice(1).map(parseCellScore));

  const allScores = matrix.flat().filter(v => v !== null);
  if (allScores.length === 0) return null;

  const minScore = Math.min(...allScores);
  const maxScore = Math.max(...allScores);
  const range = maxScore - minScore || 1;

  const cellColor = (v) => {
    if (v === null) return theme.tint;
    const t = (v - minScore) / range;
    // Color scale: low=red → mid=amber → high=green
    if (t < 0.5) {
      const localT = t * 2;
      const r = Math.round(239 + (245 - 239) * localT);
      const g = Math.round(68 + (158 - 68) * localT);
      const b = Math.round(68 + (11 - 68) * localT);
      return `rgb(${r},${g},${b})`;
    } else {
      const localT = (t - 0.5) * 2;
      const r = Math.round(245 + (16 - 245) * localT);
      const g = Math.round(158 + (185 - 158) * localT);
      const b = Math.round(11 + (129 - 11) * localT);
      return `rgb(${r},${g},${b})`;
    }
  };

  const cellW = 64, cellH = 36;
  const labelW = 80, labelH = 30;
  const W = labelW + xLabels.length * cellW + 20;
  const H = labelH + yLabels.length * cellH + 30;

  return (
    <div style={{ margin: "14px 0", padding: "14px 16px",
                  background: theme.white, border: `1px solid ${theme.divider}`, borderRadius: 10, overflowX: "auto" }}>
      <svg width={W} height={H} style={{ display: "block", minWidth: W }}>
        {/* X labels */}
        {xLabels.map((x, i) => (
          <text key={i} x={labelW + i * cellW + cellW / 2} y={20}
                textAnchor="middle"
                style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 600, fill: theme.text2 }}>
            {x}
          </text>
        ))}
        {/* Cells */}
        {matrix.map((row, ri) => (
          <g key={ri}>
            <text x={labelW - 8} y={labelH + ri * cellH + cellH * 0.62}
                  textAnchor="end"
                  style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 600, fill: theme.text2 }}>
              {row.length > 0 ? yLabels[ri] : ""}
            </text>
            {row.map((v, ci) => {
              const x = labelW + ci * cellW;
              const y = labelH + ri * cellH;
              return (
                <g key={ci}>
                  <rect x={x + 2} y={y + 2} width={cellW - 4} height={cellH - 4}
                        fill={cellColor(v)} rx="4"
                        opacity={v === null ? 0.3 : 1}/>
                  {v !== null && (
                    <text x={x + cellW / 2} y={y + cellH * 0.65}
                          textAnchor="middle"
                          style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 700,
                                   fill: (v - minScore) / range > 0.4 ? "#fff" : "#1e293b" }}>
                      {v.toFixed(2)}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        ))}
        {/* Legend */}
        <g transform={`translate(${labelW}, ${H - 14})`}>
          <text x={0} y={8} style={{ fontSize: 10, fill: theme.text4, fontFamily: fonts.mono }}>
            {minScore.toFixed(2)}
          </text>
          <defs>
            <linearGradient id="stabGrad" x1="0%" x2="100%">
              <stop offset="0%" stopColor="rgb(239,68,68)"/>
              <stop offset="50%" stopColor="rgb(245,158,11)"/>
              <stop offset="100%" stopColor="rgb(16,185,129)"/>
            </linearGradient>
          </defs>
          <rect x={40} y={0} width={150} height={8} fill="url(#stabGrad)" rx="2"/>
          <text x={200} y={8} style={{ fontSize: 10, fill: theme.text4, fontFamily: fonts.mono }}>
            {maxScore.toFixed(2)}
          </text>
        </g>
      </svg>
    </div>
  );
}

function CollapsibleTable({ title, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ margin: "8px 0" }}>
      <button onClick={() => setOpen(!open)}
              style={{ background: "none", border: "none", fontSize: 11, color: theme.text3,
                       cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: "4px 0" }}>
        <span style={{ transform: open ? "rotate(90deg)" : "rotate(0)", transition: "transform 0.15s", fontSize: 9 }}>▶</span>
        {title}
      </button>
      {open && <div style={{ marginTop: 6 }}>{children}</div>}
    </div>
  );
}


function TopTrialsChart({ headers, rows }) {
  // Expected: Rank | Trial | Score | Grade | PnL | DD | Trades | WR | PF
  const trialIdx = headers.findIndex(h => /trial/i.test(h));
  const scoreIdx = headers.findIndex(h => /score/i.test(h));
  const gradeIdx = headers.findIndex(h => /grade/i.test(h));
  const pnlIdx = headers.findIndex(h => /pnl/i.test(h));
  const ddIdx = headers.findIndex(h => /dd/i.test(h));
  const wrIdx = headers.findIndex(h => /wr/i.test(h));
  const pfIdx = headers.findIndex(h => /pf/i.test(h));
  if (scoreIdx < 0 || trialIdx < 0) return null;

  const data = rows.map((r, i) => ({
    rank: i + 1,
    trial: r[trialIdx],
    score: parseFloat(r[scoreIdx]) || 0,
    grade: gradeIdx >= 0 ? r[gradeIdx] : "?",
    pnl: pnlIdx >= 0 ? r[pnlIdx] : "",
    dd: ddIdx >= 0 ? r[ddIdx] : "",
    wr: wrIdx >= 0 ? r[wrIdx] : "",
    pf: pfIdx >= 0 ? r[pfIdx] : "",
  }));
  if (data.length === 0) return null;

  const maxScore = Math.max(...data.map(d => d.score), 0.1);
  const gradeColors = {
    A: "#10b981", B: "#3b82f6", C: "#f59e0b", D: "#f97316", F: "#ef4444", "?": "#94a3b8",
  };
  const gradeBgColors = {
    A: "rgba(16,185,129,0.08)", B: "rgba(59,130,246,0.08)",
    C: "rgba(245,158,11,0.08)", D: "rgba(249,115,22,0.08)",
    F: "rgba(239,68,68,0.08)", "?": "rgba(148,163,184,0.08)",
  };

  return (
    <div style={{ margin: "14px 0", padding: "14px 16px",
                  background: theme.white, border: `1px solid ${theme.divider}`, borderRadius: 10 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.map((d, i) => {
          const bw = (d.score / maxScore) * 100;
          const color = gradeColors[d.grade] || "#94a3b8";
          const bg = gradeBgColors[d.grade] || "rgba(148,163,184,0.08)";
          return (
            <div key={i} style={{
              display: "grid", gridTemplateColumns: "30px 60px 1fr 90px 70px 60px",
              gap: 10, alignItems: "center",
              padding: "8px 12px", borderRadius: 8,
              background: bg, borderLeft: `3px solid ${color}`,
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: theme.text4, fontFamily: fonts.mono }}>
                #{d.rank}
              </span>
              <span style={{ fontSize: 12, fontWeight: 700, color: theme.text2, fontFamily: fonts.mono }}>
                {d.trial}
              </span>
              <div style={{ position: "relative", height: 20, background: theme.tint, borderRadius: 4 }}>
                <div style={{
                  height: "100%", width: `${bw}%`,
                  background: color, borderRadius: 4,
                  transition: "width 0.4s ease",
                }}/>
                <span style={{
                  position: "absolute", left: 8, top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: 11, fontWeight: 700, fontFamily: fonts.mono,
                  color: bw > 15 ? "#fff" : theme.text, mixBlendMode: bw > 15 ? "normal" : "normal",
                  whiteSpace: "nowrap",
                }}>
                  {d.score.toFixed(3)}
                </span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, fontFamily: fonts.mono,
                             color: d.pnl.includes("-") ? theme.redText : theme.greenText, textAlign: "right" }}>
                {d.pnl}
              </span>
              <span style={{ fontSize: 11, fontFamily: fonts.mono, color: theme.redText, textAlign: "right" }}>
                DD {d.dd}
              </span>
              <span style={{
                fontSize: 10, fontWeight: 700, color: "#fff",
                background: color, padding: "2px 8px", borderRadius: 4,
                textAlign: "center",
              }}>
                {d.grade}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function BucketWaterfall({ headers, rows }) {
  // Expected: Bucket | PnL | Kumulativ | Status
  const bucketIdx = 0;
  const pnlIdx = headers.findIndex(h => /^pnl$/i.test(h));
  const cumIdx = headers.findIndex(h => /kumulativ/i.test(h));
  if (pnlIdx < 0 || cumIdx < 0) return null;

  const parsePnl = (s) => {
    const clean = s.replace(/[+$,]/g, "").trim();
    return parseFloat(clean) || 0;
  };

  const data = rows.map(r => ({
    bucket: r[bucketIdx],
    pnl: parsePnl(r[pnlIdx]),
    cum: parsePnl(r[cumIdx]),
    pnlStr: r[pnlIdx],
    cumStr: r[cumIdx],
  }));
  if (data.length === 0) return null;

  const maxCum = Math.max(...data.map(d => Math.abs(d.cum)), 1);

  const W = 760;
  const chartH = 280;
  const padL = 70, padR = 80, padT = 50, padB = 60;
  const innerW = W - padL - padR;
  const innerH = chartH - padT - padB;
  const barW = Math.min(70, innerW / data.length * 0.55);
  const stepW = innerW / data.length;

  const valueToY = (v) => padT + innerH - (v / maxCum) * innerH * 0.85;
  const zeroY = valueToY(0);

  const cumPoints = data.map((d, i) =>
    `${padL + stepW * i + stepW / 2},${valueToY(d.cum)}`
  ).join(" ");

  return (
    <div style={{ margin: "14px 0", padding: "14px 16px",
                  background: theme.white, border: `1px solid ${theme.divider}`, borderRadius: 10 }}>
      <svg width="100%" viewBox={`0 0 ${W} ${chartH}`} style={{ display: "block" }}>
        {/* Zero line */}
        <line x1={padL} y1={zeroY} x2={padL + innerW} y2={zeroY}
              stroke={theme.border} strokeDasharray="3,3" strokeWidth="1"/>

        {/* Bars (waterfall) — label BELOW the bucket axis */}
        {data.map((d, i) => {
          const cx = padL + stepW * i + stepW / 2;
          const barX = cx - barW / 2;
          const isPositive = d.pnl >= 0;
          const color = isPositive ? "#10b981" : "#ef4444";
          const barH = Math.abs((d.pnl / maxCum) * innerH * 0.85);
          const barY = isPositive ? zeroY - barH : zeroY;

          return (
            <g key={i}>
              <rect x={barX} y={barY} width={barW} height={Math.max(barH, 2)}
                    fill={color} fillOpacity="0.85" rx="3"/>
              {/* Bucket label at bottom */}
              <text x={cx} y={chartH - 32}
                    textAnchor="middle"
                    style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 600, fill: theme.text3 }}>
                Bucket {d.bucket}
              </text>
              {/* Bucket PnL value at bottom, below bucket label */}
              <text x={cx} y={chartH - 14}
                    textAnchor="middle"
                    style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 700,
                             fill: isPositive ? theme.greenText : theme.redText }}>
                {d.pnlStr}
              </text>
            </g>
          );
        })}

        {/* Cumulative line — labels ABOVE each point to avoid overlap with bars */}
        <polyline points={cumPoints}
                  fill="none" stroke="#3b82f6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        {data.map((d, i) => {
          const cx = padL + stepW * i + stepW / 2;
          const cy = valueToY(d.cum);
          // Offset label away from bar top to prevent collision
          const labelY = cy - 12;
          return (
            <g key={`c${i}`}>
              <circle cx={cx} cy={cy} r="4.5" fill="#3b82f6" stroke={theme.white} strokeWidth="2"/>
              <rect x={cx - 35} y={labelY - 12} width="70" height="16" rx="3"
                    fill={theme.white} stroke="#3b82f6" strokeWidth="1" opacity="0.95"/>
              <text x={cx} y={labelY}
                    textAnchor="middle"
                    style={{ fontSize: 10, fontFamily: fonts.mono, fontWeight: 700, fill: "#3b82f6" }}>
                {d.cumStr}
              </text>
            </g>
          );
        })}

        {/* Y-axis labels */}
        <text x={padL - 8} y={padT + 6} textAnchor="end"
              style={{ fontSize: 10, fontFamily: fonts.mono, fill: theme.text4 }}>
          ${Math.round(maxCum * 0.85 / 1000)}k
        </text>
        <text x={padL - 8} y={zeroY + 4} textAnchor="end"
              style={{ fontSize: 10, fontFamily: fonts.mono, fill: theme.text4 }}>
          $0
        </text>
      </svg>
      <div style={{ display: "flex", gap: 16, fontSize: 10, color: theme.text3, marginTop: 4, paddingLeft: padL }}>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#10b981", borderRadius: 2, marginRight: 4, verticalAlign: "middle" }}/>Bucket-PnL positiv</span>
        <span><span style={{ display: "inline-block", width: 10, height: 10, background: "#ef4444", borderRadius: 2, marginRight: 4, verticalAlign: "middle" }}/>Bucket-PnL negativ</span>
        <span><span style={{ display: "inline-block", width: 16, height: 2, background: "#3b82f6", marginRight: 4, verticalAlign: "middle" }}/>Kumulativ (über Bars)</span>
      </div>
    </div>
  );
}


function ImportanceBars({ headers, rows }) {
  // Expected: | Rank | Parameter | Importance | Balken |
  const paramIdx = headers.findIndex(h => /parameter/i.test(h));
  const impIdx = headers.findIndex(h => /importance|impact|wichtigkeit/i.test(h));
  if (impIdx < 0 || paramIdx < 0) return null;

  const data = rows.map(r => ({
    param: r[paramIdx].replace(/[`*]/g, ""),
    importance: parseFloat(r[impIdx]) || 0,
    raw: r[impIdx],
  })).filter(d => d.importance > 0).sort((a, b) => b.importance - a.importance);

  if (data.length === 0) return null;
  const maxImp = Math.max(...data.map(d => d.importance));

  return (
    <div style={{ margin: "14px 0", padding: "14px 18px",
                  background: theme.white, border: `1px solid ${theme.divider}`, borderRadius: 10 }}>
      {data.map((d, i) => {
        const barPct = (d.importance / maxImp) * 100;
        const hue = 220 + (i / data.length) * 40;
        const sat = 70 - (i / data.length) * 40;
        const color = `hsl(${hue}, ${sat}%, 55%)`;
        return (
          <div key={i} style={{ marginBottom: i === data.length - 1 ? 0 : 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
              <span style={{ fontSize: 12, fontFamily: fonts.mono, fontWeight: 600, color: theme.text }}>
                {d.param}
              </span>
              <span style={{ fontSize: 11, fontFamily: fonts.mono, color: theme.text3 }}>
                {d.raw}
              </span>
            </div>
            <div style={{ height: 10, background: theme.tint, borderRadius: 3, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${barPct}%`, background: color, borderRadius: 3, transition: "width 0.4s ease" }}/>
            </div>
          </div>
        );
      })}
    </div>
  );
}


function ClusterCards({ headers, rows }) {
  // Expected: | Cluster | Size | Ø Score | Ø PnL | Ø DD | Ø Trades | Dominante Params |
  const sizeIdx = headers.findIndex(h => /size/i.test(h));
  const scoreIdx = headers.findIndex(h => /score/i.test(h));
  const pnlIdx = headers.findIndex(h => /pnl/i.test(h));
  const ddIdx = headers.findIndex(h => /dd/i.test(h));
  const tradesIdx = headers.findIndex(h => /trades/i.test(h));
  const paramsIdx = headers.findIndex(h => /dominant|param/i.test(h));
  if (scoreIdx < 0) return null;

  const data = rows.map(r => ({
    cluster: r[0],
    size: sizeIdx >= 0 ? parseInt(r[sizeIdx]) || 0 : 0,
    score: parseFloat(r[scoreIdx]) || 0,
    pnl: pnlIdx >= 0 ? r[pnlIdx] : "",
    dd: ddIdx >= 0 ? r[ddIdx] : "",
    trades: tradesIdx >= 0 ? r[tradesIdx] : "",
    params: paramsIdx >= 0 ? r[paramsIdx] : "",
  }));
  if (data.length === 0) return null;

  const maxScore = Math.max(...data.map(d => d.score), 0.1);
  const maxSize = Math.max(...data.map(d => d.size), 1);
  const bestIdx = data.reduce((best, d, i) => d.score > data[best].score ? i : best, 0);

  // Distinct cluster colors (up to 8)
  const clusterColors = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "#84cc16"];

  return (
    <div style={{ margin: "14px 0",
                  display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
      {data.map((d, i) => {
        const color = clusterColors[i % clusterColors.length];
        const isBest = i === bestIdx;
        const scoreBarW = (d.score / maxScore) * 100;
        const sizeBarW = (d.size / maxSize) * 100;

        // Parse dominant params into chips
        const paramChips = d.params.split(",").map(p => p.trim()).filter(Boolean);

        return (
          <div key={i} style={{
            padding: "14px 16px",
            background: theme.white, borderRadius: 10,
            border: isBest ? `2px solid ${color}` : `1px solid ${theme.divider}`,
            boxShadow: isBest ? `0 2px 12px rgba(16,185,129,0.15)` : "none",
            position: "relative",
          }}>
            {isBest && (
              <div style={{
                position: "absolute", top: -10, right: 12,
                padding: "2px 10px", borderRadius: 10,
                background: color, color: "#fff",
                fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
              }}>★ Best</div>
            )}

            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
              <span style={{
                fontSize: 15, fontWeight: 800, color,
                fontFamily: fonts.mono,
              }}>
                {d.cluster}
              </span>
              <span style={{ fontSize: 11, color: theme.text4, fontFamily: fonts.mono }}>
                {d.size} Trials
              </span>
            </div>

            {/* Score bar */}
            <div style={{ marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: theme.text4, marginBottom: 3 }}>
                <span>Ø Score</span>
                <span style={{ fontFamily: fonts.mono, fontWeight: 700, color: theme.text }}>{d.score.toFixed(3)}</span>
              </div>
              <div style={{ height: 6, background: theme.tint, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${scoreBarW}%`, background: color, borderRadius: 3 }}/>
              </div>
            </div>

            {/* Metrics grid */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 9, color: theme.text4, textTransform: "uppercase", letterSpacing: 0.5 }}>PnL</div>
                <div style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 700,
                              color: d.pnl.includes("-") ? theme.redText : theme.greenText }}>
                  {d.pnl || "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: theme.text4, textTransform: "uppercase", letterSpacing: 0.5 }}>DD</div>
                <div style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 700, color: theme.redText }}>
                  {d.dd || "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 9, color: theme.text4, textTransform: "uppercase", letterSpacing: 0.5 }}>Trades</div>
                <div style={{ fontSize: 11, fontFamily: fonts.mono, fontWeight: 700, color: theme.text }}>
                  {d.trades || "—"}
                </div>
              </div>
            </div>

            {/* Dominant params as chips */}
            {paramChips.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, paddingTop: 8, borderTop: `1px solid ${theme.divider}` }}>
                {paramChips.map((p, pi) => (
                  <span key={pi} style={{
                    padding: "2px 8px", borderRadius: 10,
                    background: theme.tint,
                    fontSize: 10, fontFamily: fonts.mono, color: theme.text2,
                  }}>
                    {p}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function NegativeCombosBars({ headers, rows }) {
  // Expected: | Parameter | p-Value | Verdächtige Werte |
  const paramIdx = headers.findIndex(h => /parameter/i.test(h));
  const pvalIdx = headers.findIndex(h => /p-?value/i.test(h));
  const suspectIdx = headers.findIndex(h => /verdächtig|suspicious/i.test(h));
  if (pvalIdx < 0) return null;

  const data = rows.map(r => {
    const pStr = r[pvalIdx].replace(/[`*]/g, "").trim();
    const pval = parseFloat(pStr) || 1;
    // -log10(p) for bar height (smaller p = higher significance = longer bar)
    const significance = -Math.log10(Math.max(pval, 1e-10));
    return {
      param: r[paramIdx].replace(/[`*]/g, ""),
      pval,
      pStr,
      significance,
      suspects: suspectIdx >= 0 ? r[suspectIdx].replace(/[`]/g, "") : "",
    };
  }).sort((a, b) => b.significance - a.significance);

  if (data.length === 0) return null;
  const maxSig = Math.max(...data.map(d => d.significance), 1);

  return (
    <div style={{ margin: "14px 0", padding: "14px 16px",
                  background: theme.white, border: `1px solid ${theme.divider}`, borderRadius: 10 }}>
      <div style={{ fontSize: 11, color: theme.text3, marginBottom: 12, fontStyle: "italic" }}>
        Sortiert nach Signifikanz (niedrigere p-Werte = starker Hinweis). Farbe zeigt Stärke:
        <span style={{ color: "#dc2626", fontWeight: 700 }}> rot</span> p &lt; 0.001,
        <span style={{ color: "#ea580c", fontWeight: 700 }}> orange</span> p &lt; 0.01,
        <span style={{ color: "#d97706", fontWeight: 700 }}> amber</span> p &lt; 0.05.
      </div>
      {data.map((d, i) => {
        const barPct = (d.significance / maxSig) * 100;
        const color = d.pval < 0.001 ? "#dc2626" : d.pval < 0.01 ? "#ea580c" : d.pval < 0.05 ? "#d97706" : "#94a3b8";
        return (
          <div key={i} style={{ marginBottom: i === data.length - 1 ? 0 : 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
              <span style={{ fontSize: 12, fontFamily: fonts.mono, fontWeight: 700, color: theme.text }}>
                {d.param}
              </span>
              <span style={{ fontSize: 11, fontFamily: fonts.mono, color }}>
                p = {d.pStr}
              </span>
            </div>
            <div style={{ height: 8, background: theme.tint, borderRadius: 3, overflow: "hidden", marginBottom: 4 }}>
              <div style={{ height: "100%", width: `${barPct}%`, background: color, borderRadius: 3, transition: "width 0.4s ease" }}/>
            </div>
            {d.suspects && (
              <div style={{ fontSize: 10.5, color: theme.text3, fontFamily: fonts.mono, paddingLeft: 4 }}>
                ⚠ {d.suspects}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function ExecutiveSummaryCards({ items }) {
  // `items` is an array of strings (numbered list items under "Executive Summary" H3)
  // Each item typically has: "**67% der gültigen Trials sind Grade A** — text"
  // We extract: the bold headline + the description
  if (!items || items.length === 0) return null;

  // Color rotation for the cards
  const accents = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6"];

  return (
    <div style={{ margin: "14px 0",
                  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
      {items.map((it, i) => {
        const accent = accents[i % accents.length];
        // Try to split "**Headline** — rest" or "**Headline** - rest"
        const match = it.match(/^\*\*(.+?)\*\*\s*[—–-]\s*(.*)$/);
        const headline = match ? match[1] : it.replace(/\*\*/g, "");
        const detail = match ? match[2] : "";
        return (
          <div key={i} style={{
            padding: "14px 16px",
            background: theme.white, borderRadius: 10,
            border: `1px solid ${theme.divider}`,
            borderTop: `3px solid ${accent}`,
            display: "flex", flexDirection: "column", gap: 6,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.text, lineHeight: 1.4 }}>
              {headline}
            </div>
            {detail && (
              <div style={{ fontSize: 11.5, color: theme.text2, lineHeight: 1.55 }}>
                {detail}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function parseTable(lines, startIdx) {
  // Parse markdown table starting at startIdx
  // Returns { headers, rows, alignments, nextIdx }
  const headerLine = lines[startIdx];
  const sepLine = lines[startIdx + 1];
  if (!sepLine || !sepLine.match(/^\s*\|[-:|\s]+\|\s*$/)) return null;

  const parseRow = (line) => line.trim().replace(/^\||\|$/g, "").split("|").map(c => c.trim());
  const headers = parseRow(headerLine);
  const seps = parseRow(sepLine);
  const alignments = seps.map(s => {
    if (s.endsWith(":") && s.startsWith(":")) return "center";
    if (s.endsWith(":")) return "right";
    return "left";
  });

  const rows = [];
  let i = startIdx + 2;
  while (i < lines.length && lines[i].match(/^\s*\|.*\|\s*$/)) {
    rows.push(parseRow(lines[i]));
    i++;
  }
  return { headers, rows, alignments, nextIdx: i };
}

// PnL detection for coloring cells
function renderCell(content, align, rowIdx, colIdx) {
  const isPnl = /^[-+]?\$[\d,]+$/.test(content) || /^[-+]\$/.test(content);
  const isPct = /^[-+]?\d+\.?\d*%$/.test(content);
  const isNegative = content.startsWith("-") && (isPnl || isPct);
  const isPositive = content.startsWith("+") && (isPnl || isPct);

  let color = theme.text2;
  let weight = 500;
  if (isNegative) { color = theme.redText; weight = 600; }
  else if (isPositive) { color = theme.greenText; weight = 600; }

  // Render inline formatting (code, bold)
  return <span style={{color, fontWeight: weight, fontFamily: (isPnl || isPct) ? fonts.mono : fonts.body}}>
    {renderInline(content)}
  </span>;
}

function MarkdownView({ markdown }) {
  const blocks = useMemo(() => {
    if (!markdown) return [];
    const lines = markdown.split("\n");
    const out = [];
    let i = 0;
    let listBuf = null;   // { type: "ul"|"ol", items: [] }

    const flushList = () => {
      if (listBuf) { out.push(listBuf); listBuf = null; }
    };

    while (i < lines.length) {
      const line = lines[i];

      // Skip HTML comments
      if (line.match(/^<!--.*-->$/)) { i++; continue; }

      // Horizontal rule
      if (line.match(/^---+$/)) { flushList(); out.push({ type: "hr" }); i++; continue; }

      // Headings
      const h = line.match(/^(#{1,4})\s+(.*)$/);
      if (h) {
        flushList();
        out.push({ type: "heading", level: h[1].length, text: h[2] });
        i++;
        continue;
      }

      // Table
      if (line.match(/^\s*\|.*\|\s*$/)) {
        flushList();
        const t = parseTable(lines, i);
        if (t) {
          out.push({ type: "table", ...t });
          i = t.nextIdx;
          continue;
        }
      }

      // Bullet list
      const bullet = line.match(/^\s*[-*]\s+(.*)$/);
      if (bullet) {
        if (!listBuf || listBuf.type !== "ul") { flushList(); listBuf = { type: "ul", items: [] }; }
        listBuf.items.push(bullet[1]);
        i++;
        continue;
      }

      // Numbered list
      const numbered = line.match(/^\s*(\d+)\.\s+(.*)$/);
      if (numbered) {
        if (!listBuf || listBuf.type !== "ol") { flushList(); listBuf = { type: "ol", items: [] }; }
        listBuf.items.push(numbered[2]);
        i++;
        continue;
      }

      // Blockquote (italic hint lines) — lines starting with _ or __
      const italic = line.match(/^_([^_].*)_$/);
      if (italic) {
        flushList();
        out.push({ type: "hint", text: italic[1] });
        i++;
        continue;
      }

      // Empty line
      if (line.trim() === "") {
        flushList();
        i++;
        continue;
      }

      // Paragraph
      flushList();
      out.push({ type: "p", text: line });
      i++;
    }
    flushList();
    return out;
  }, [markdown]);

  // TOC from h2 headings
  const toc = useMemo(() =>
    blocks.filter(b => b.type === "heading" && b.level === 2)
      .map((b, idx) => ({ text: b.text, anchor: `h-${idx}` })),
    [blocks]
  );

  let h2Counter = 0;
  let currentSection = "";          // tracks current H2 section
  let currentSubsection = "";        // tracks current H3 subsection
  let lastH3Text = "";               // last H3 text (for sensitivity param name)
  let sensitivityParamBuffer = "";   // for "Parameter-Sensitivität" sub-tables

  return <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: 20 }}>
    {/* TOC */}
    <div style={{ position: "sticky", top: 16, alignSelf: "start", maxHeight: "calc(100vh - 40px)", overflowY: "auto" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Inhalt</div>
      {toc.map((t, idx) => (
        <a key={idx} href={`#${t.anchor}`} style={{
          display: "block", padding: "6px 10px", marginBottom: 2,
          fontSize: 12, color: theme.text2, textDecoration: "none",
          borderRadius: 6, borderLeft: `2px solid transparent`,
        }} onMouseEnter={e => { e.target.style.background = theme.tint; e.target.style.borderLeftColor = theme.blue; }}
           onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.borderLeftColor = "transparent"; }}>
          {t.text}
        </a>
      ))}
    </div>

    {/* Main content */}
    <div style={{ minWidth: 0 }}>
      {blocks.map((b, idx) => {
        if (b.type === "heading") {
          const anchor = b.level === 2 ? `h-${h2Counter++}` : undefined;
          if (b.level === 2) {
            currentSection = b.text;
            currentSubsection = "";
          } else if (b.level === 3) {
            currentSubsection = b.text;
            lastH3Text = b.text;
          }
          if (b.level === 1) {
            return <h1 key={idx} style={{ fontSize: 22, fontWeight: 700, color: theme.text, margin: "0 0 16px", fontFamily: fonts.serif }}>{b.text}</h1>;
          }
          if (b.level === 2) {
            return <h2 key={idx} id={anchor} style={{
              fontSize: 17, fontWeight: 700, color: theme.text, margin: "28px 0 12px",
              paddingBottom: 8, borderBottom: `1px solid ${theme.divider}`, fontFamily: fonts.serif,
              scrollMarginTop: 16,
            }}>{b.text}</h2>;
          }
          if (b.level === 3) {
            // For Parameter-Sensitivität, H3 is the param name (e.g. `sl_atr_mult`)
            // We keep it as a header but make it look like a sub-label above the chart
            if (/Sensitivität/.test(currentSection)) {
              return null;  // we'll inject the H3 text into the chart itself
            }
            return <h3 key={idx} style={{ fontSize: 14, fontWeight: 700, color: theme.text, margin: "16px 0 8px", fontFamily: fonts.body }}>{b.text}</h3>;
          }
          return <h4 key={idx} style={{ fontSize: 13, fontWeight: 600, color: theme.text2, margin: "12px 0 6px" }}>{b.text}</h4>;
        }
        if (b.type === "hr") {
          return <hr key={idx} style={{ border: "none", borderTop: `1px solid ${theme.divider}`, margin: "20px 0" }} />;
        }
        if (b.type === "hint") {
          return <div key={idx} style={{
            fontSize: 12, color: theme.text3, fontStyle: "italic",
            padding: "8px 12px", margin: "8px 0",
            borderLeft: `3px solid ${theme.border}`,
            background: theme.tint,
            borderRadius: "0 6px 6px 0",
          }}>{renderInline(b.text)}</div>;
        }
        if (b.type === "p") {
          return <p key={idx} style={{ fontSize: 13, lineHeight: 1.65, color: theme.text2, margin: "8px 0" }}>
            {renderInline(b.text)}
          </p>;
        }
        if (b.type === "ul" || b.type === "ol") {
          // Special: Executive Summary under Zusammenfassung → render as KPI cards
          if (b.type === "ol" && /Executive Summary/i.test(currentSubsection)) {
            return <ExecutiveSummaryCards key={idx} items={b.items}/>;
          }
          const Tag = b.type === "ul" ? "ul" : "ol";
          return <Tag key={idx} style={{ margin: "8px 0", paddingLeft: 24 }}>
            {b.items.map((item, i) => (
              <li key={i} style={{ fontSize: 13, lineHeight: 1.65, color: theme.text2, margin: "4px 0" }}>
                {renderInline(item)}
              </li>
            ))}
          </Tag>;
        }
        if (b.type === "table") {
          // Context-aware chart rendering
          let chart = null;
          let tableTitle = "Tabelle anzeigen";

          if (/Grade-Verteilung/i.test(currentSection)) {
            chart = <GradeDonut headers={b.headers} rows={b.rows}/>;
            tableTitle = "Details (Tabelle) anzeigen";
          } else if (/Top-?\d*\s*Trials/i.test(currentSection)) {
            chart = <TopTrialsChart headers={b.headers} rows={b.rows}/>;
            tableTitle = "Rohdaten mit allen Metriken anzeigen";
          } else if (/Sensitivität/i.test(currentSection) && lastH3Text) {
            chart = <SensitivityBars paramName={lastH3Text} headers={b.headers} rows={b.rows}/>;
            tableTitle = "Rohdaten anzeigen";
          } else if (/Hard-Constraint Violations/i.test(currentSection) && /nach Regel|Regel/i.test(currentSubsection)) {
            chart = <ViolationsBars headers={b.headers} rows={b.rows}/>;
            tableTitle = "Details anzeigen";
          } else if (/Stabilitäts-Zonen/i.test(currentSection)) {
            chart = <StabilityHeatmap headers={b.headers} rows={b.rows}/>;
            tableTitle = "Rohwerte als Tabelle";
          } else if (/Score-Breakdown/i.test(currentSection) && /Bucket/i.test(currentSubsection)) {
            chart = <BucketWaterfall headers={b.headers} rows={b.rows}/>;
            tableTitle = "Details anzeigen";
          } else if (/Parameter-Importance/i.test(currentSection)) {
            chart = <ImportanceBars headers={b.headers} rows={b.rows}/>;
            tableTitle = "Details anzeigen";
          } else if (/Cluster-Analyse/i.test(currentSection)) {
            chart = <ClusterCards headers={b.headers} rows={b.rows}/>;
            tableTitle = "Details (Tabelle) anzeigen";
          } else if (/Negativ-Kombinationen/i.test(currentSection)) {
            chart = <NegativeCombosBars headers={b.headers} rows={b.rows}/>;
            tableTitle = "Details anzeigen";
          }

          const tableEl = (
            <div style={{ overflowX: "auto", margin: "12px 0", borderRadius: 8, border: `1px solid ${theme.border}` }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: theme.tint }}>
                    {b.headers.map((h, i) => (
                      <th key={i} style={{
                        padding: "8px 12px", textAlign: b.alignments[i] || "left",
                        fontWeight: 700, color: theme.text, fontSize: 11,
                        borderBottom: `1px solid ${theme.border}`,
                        whiteSpace: "nowrap",
                      }}>{renderInline(h)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((r, ri) => (
                    <tr key={ri} style={{ borderBottom: `1px solid ${theme.divider}` }}>
                      {r.map((c, ci) => (
                        <td key={ci} style={{
                          padding: "6px 12px", textAlign: b.alignments[ci] || "left",
                          fontSize: 12, verticalAlign: "top", whiteSpace: "nowrap",
                        }}>{renderCell(c, b.alignments[ci], ri, ci)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );

          // If chart exists, wrap table in collapsible
          if (chart) {
            return <div key={idx}>
              {chart}
              <CollapsibleTable title={tableTitle}>{tableEl}</CollapsibleTable>
            </div>;
          }
          return <div key={idx}>{tableEl}</div>;
        }
        return null;
      })}
    </div>
  </div>;
}

// ─── Main Component ─────────────────────────────────
export default function AnalyzeTab({ stratFile }) {
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);  // full run object
  const [runType, setRunType] = useState("walkforward");

  // Config state
  const [sections, setSections] = useState(() => {
    const out = {};
    WF_SECTIONS.forEach(s => out[s.id] = s.default);
    return out;
  });
  const [filterExpr, setFilterExpr] = useState("");
  const [topN, setTopN] = useState(10);
  const [crossValidate, setCrossValidate] = useState(false);
  const [tableOnly, setTableOnly] = useState(false);
  const [mcEnabled, setMcEnabled] = useState(false);
  const [mcTop, setMcTop] = useState(100);
  const [includeInvalid, setIncludeInvalid] = useState(false);  // v4-FEAT: advanced
  const [reportTitle, setReportTitle] = useState("");           // v4-FEAT: optional save title

  // Result state
  const [loading, setLoading] = useState(false);
  const [markdown, setMarkdown] = useState("");
  const [error, setError] = useState(null);

  // v4-FEAT: Saved reports (persistent)
  const [savedReports, setSavedReports] = useState([]);
  const [currentSavedId, setCurrentSavedId] = useState(null);  // null = fresh/unsaved, id = viewing saved
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  // MC polling state
  const [mcStatus, setMcStatus] = useState(null);

  // v4-FEAT-C: Compare state
  const [compareRun, setCompareRun] = useState(null);    // second run to compare against
  const [compareResult, setCompareResult] = useState(null);
  const [comparing, setComparing] = useState(false);

  // v4-FEAT-B: Apply state
  const [applyStrategyFile, setApplyStrategyFile] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyPreview, setApplyPreview] = useState(null);   // { source, params, n_params }

  // v5-EXPERIMENTS: state
  const [experiments, setExperiments] = useState([]);
  const [expModalOpen, setExpModalOpen] = useState(false);
  const [expBaseYaml, setExpBaseYaml] = useState("");
  const [expName, setExpName] = useState("");
  const [expChanges, setExpChanges] = useState([]);   // [{path, value, description}] — legacy expert mode
  const [expNote, setExpNote] = useState("");
  const [expCreating, setExpCreating] = useState(false);
  const [strategyFiles, setStrategyFiles] = useState([]);

  // v6-SMART-BUILDER: schema-based form state
  const [expSchema, setExpSchema] = useState(null);         // loaded schema for current base YAML
  const [expSchemaLoading, setExpSchemaLoading] = useState(false);
  const [expFieldEdits, setExpFieldEdits] = useState({});   // { [path]: { enabled: bool, newValue: any } }
  const [expOpenSections, setExpOpenSections] = useState({ hard_constraints_is: true });
  const [expExpertMode, setExpExpertMode] = useState(false);

  async function refreshExperiments() {
    try {
      const r = await fetch(`${API_BASE}/experiments/list?_t=${Date.now()}`);
      if (r.ok) {
        const d = await r.json();
        setExperiments(d?.experiments || []);
      }
    } catch {}
  }

  async function refreshStrategyFiles() {
    try {
      const r = await fetch(`${API_BASE}/strategy-files?_t=${Date.now()}`);
      if (r.ok) {
        const d = await r.json();
        setStrategyFiles((d?.files || []).filter(f => f.status !== "archived"));
      }
    } catch {}
  }

  async function createExperiment() {
    if (!expBaseYaml || !expName) {
      toast("Base YAML und Name benötigt", "error");
      return;
    }
    setExpCreating(true);
    try {
      const body = {
        base_yaml: expBaseYaml,
        experiment_name: expName,
        derived_from_run: selectedRun ? `${selectedRun.run_type === "optimizer" ? "opt" : "wf"}_${selectedRun.run_id || selectedRun.id || "?"}` : null,
        run_file: selectedRun?.path || null,
        changes: expChanges.filter(c => c.path && c.value !== undefined && c.value !== ""),
        note: expNote || null,
      };
      const r = await fetch(`${API_BASE}/experiments/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok) {
        toast(`Experiment "${d.filename}" erstellt`, "success");
        setExpModalOpen(false);
        setExpChanges([]);
        setExpName("");
        setExpNote("");
        refreshExperiments();
      } else {
        toast(d.detail || "Erstellen fehlgeschlagen", "error");
      }
    } catch (e) {
      toast(String(e), "error");
    } finally {
      setExpCreating(false);
    }
  }

  async function deleteExperiment(filename) {
    if (!window.confirm(`Experiment "${filename}" löschen?`)) return;
    try {
      await fetch(`${API_BASE}/experiments/${encodeURIComponent(filename)}`, { method: "DELETE" });
      toast("Experiment gelöscht", "info");
      refreshExperiments();
    } catch { toast("Fehler", "error"); }
  }

  async function promoteExperiment(filename) {
    if (!window.confirm(`"${filename}" nach Haupt-YAML promoten?\n\nEin Backup der bisherigen Haupt-YAML wird automatisch angelegt.`)) return;
    try {
      const r = await fetch(`${API_BASE}/experiments/promote/${encodeURIComponent(filename)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ backup: true }),
      });
      const d = await r.json();
      if (r.ok) {
        toast(`Promoted → ${d.main_yaml}`, "success");
        refreshExperiments();
      } else {
        toast(d.detail || "Promote fehlgeschlagen", "error");
      }
    } catch { toast("Fehler", "error"); }
  }

  useEffect(() => {
    refreshExperiments();
    refreshStrategyFiles();
  }, []);

  // Pre-populate base YAML from selected run when opening modal
  useEffect(() => {
    if (expModalOpen && !expBaseYaml) {
      // Best guess: mgc_custom_ibkr.yaml if instrument is MGC, etc.
      const instr = selectedRun?.instrument || selectedRun?.symbol || "";
      const guess = strategyFiles.find(f => f.instrument === instr) || strategyFiles[0];
      if (guess) setExpBaseYaml(guess.filename);
      // Auto-suggest name
      if (!expName) {
        const ts = new Date().toISOString().replace(/[^0-9]/g, "").slice(0, 12);
        setExpName(`test_${ts}`);
      }
    }
  }, [expModalOpen, strategyFiles, selectedRun]);

  // v6-SMART-BUILDER: fetch schema when base YAML changes
  useEffect(() => {
    if (!expModalOpen || !expBaseYaml) {
      setExpSchema(null);
      setExpFieldEdits({});
      return;
    }
    setExpSchemaLoading(true);
    fetch(`${API_BASE}/strategy-schema/${encodeURIComponent(expBaseYaml)}?_t=${Date.now()}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(data => {
        setExpSchema(data);
        // reset edits when base YAML changes
        setExpFieldEdits({});
      })
      .catch(err => {
        console.warn("Schema fetch failed:", err);
        setExpSchema(null);
      })
      .finally(() => setExpSchemaLoading(false));
  }, [expModalOpen, expBaseYaml]);

  // v6-SMART-BUILDER: build expChanges from expFieldEdits whenever they change
  useEffect(() => {
    if (!expSchema) return;
    const activeChanges = [];
    for (const cat of expSchema.categories || []) {
      for (const field of cat.fields || []) {
        const edit = expFieldEdits[field.path];
        if (!edit?.enabled) continue;
        if (field.type === "range_triplet") {
          // Split range_triplet into 3 flat paths
          const nv = edit.newValue || {};
          ["min", "max", "step"].forEach(k => {
            if (nv[k] !== undefined && nv[k] !== null && nv[k] !== "") {
              activeChanges.push({
                path: field.sub_paths[k],
                value: Number(nv[k]),
                description: `${field.label} (${k})`,
              });
            }
          });
        } else {
          activeChanges.push({
            path: field.path,
            value: edit.newValue,
            description: field.label,
          });
        }
      }
    }
    setExpChanges(activeChanges);
  }, [expFieldEdits, expSchema]);

  // Load runs list
  const refreshRuns = useCallback(async () => {
    try {
      const res = await getApi("/analyze/runs");
      const runs = res?.runs || [];
      setRuns(runs);
      // Auto-select latest WF by default
      if (!selectedRun && runs.length > 0) {
        const wf = runs.find(r => r.run_type === "walkforward") || runs[0];
        setSelectedRun(wf);
        setRunType(wf.run_type);
      }
    } catch (e) {
      console.error("Failed to load runs:", e);
    }
  }, [selectedRun]);

  useEffect(() => { refreshRuns(); }, []);

  // Poll MC status when MC is running
  useEffect(() => {
    if (!mcStatus?.running) return;
    const id = setInterval(async () => {
      try {
        const s = await getApi("/analyze/status");
        setMcStatus(s);
        if (!s.running) {
          clearInterval(id);
          if (s.result) {
            setMarkdown(s.result);
            setCurrentSavedId(s?.saved?.report_id || null);
            toast("MC-Analyse abgeschlossen" + (s?.saved ? " · gespeichert" : ""), "success");
            refreshSavedReports();
            setReportTitle("");
          } else if (s.error) {
            setError(s.error);
            toast("MC-Analyse fehlgeschlagen", "error");
          }
        }
      } catch (e) {
        console.error("MC status poll failed:", e);
      }
    }, 3000);
    return () => clearInterval(id);
  }, [mcStatus?.running]);

  // Switch sections when run type changes
  useEffect(() => {
    const defs = runType === "optimizer" ? OPT_SECTIONS : WF_SECTIONS;
    const out = {};
    defs.forEach(s => out[s.id] = s.default);
    setSections(out);
  }, [runType]);

  // v4-FEAT: Load saved reports when run changes
  async function refreshSavedReports(runPath) {
    const target = runPath || selectedRun?.path;
    if (!target) { setSavedReports([]); return; }
    try {
      // Direct fetch to avoid getApi's ?_t= timestamp suffix breaking our query string
      const r = await fetch(`${API_BASE}/analyze/reports?run_file=${encodeURIComponent(target)}&_t=${Date.now()}`);
      if (!r.ok) { setSavedReports([]); return; }
      const res = await r.json();
      setSavedReports(res?.reports || []);
    } catch (e) {
      console.error("Failed to load saved reports:", e);
      setSavedReports([]);
    }
  }

  useEffect(() => {
    if (selectedRun) {
      refreshSavedReports(selectedRun.path);
      // Reset viewing state when run changes
      setCurrentSavedId(null);
      setMarkdown("");
    }
  }, [selectedRun?.path]);

  // Switch run type when run selection changes
  useEffect(() => {
    if (selectedRun) setRunType(selectedRun.run_type);
  }, [selectedRun]);

  const availableSections = runType === "optimizer" ? OPT_SECTIONS : WF_SECTIONS;
  const selectedSections = Object.entries(sections).filter(([, v]) => v).map(([k]) => k);

  async function runAnalysis() {
    setError(null);
    setMarkdown("");

    const req = {
      run_file: selectedRun?.path || selectedRun?.file,
      run_type: runType,
      sections: selectedSections.length < availableSections.length ? selectedSections : null,
      filter_expr: filterExpr || null,
      top_n: topN,
      cross_validate: crossValidate && runType === "walkforward",
      table_only: tableOnly,
      include_invalid: includeInvalid,
      title: reportTitle || null,
      save: true,
    };

    if (mcEnabled && runType === "optimizer") {
      // Long-running MC path
      setLoading(true);
      try {
        await postApi("/analyze/report/mc", {
          run_file: req.run_file,
          mc_top: mcTop,
          sections: req.sections,
          include_invalid: includeInvalid,
          title: reportTitle || null,
        });
        toast(`MC-Analyse gestartet (ca. ${Math.ceil(mcTop / 6)} Min)`, "info");
        const s = await getApi("/analyze/status");
        setMcStatus(s);
      } catch (e) {
        setError(String(e.message || e));
        toast("MC konnte nicht gestartet werden", "error");
      } finally {
        setLoading(false);
      }
      return;
    }

    // Fast path
    setLoading(true);
    try {
      const res = await postApi("/analyze/report", req);
      if (res?.markdown) {
        setMarkdown(res.markdown);
        setCurrentSavedId(res?.saved?.report_id || null);
        toast("Analyse fertig" + (res?.saved ? " · gespeichert" : ""), "success");
        // Refresh saved list
        refreshSavedReports();
        // Clear title input for next run
        setReportTitle("");
      } else if (res?.detail) {
        setError(res.detail);
      } else {
        setError("Keine Markdown-Ausgabe");
      }
    } catch (e) {
      setError(String(e.message || e));
      toast("Analyse fehlgeschlagen", "error");
    } finally {
      setLoading(false);
    }
  }

  // v4-FEAT: Saved report handlers
  async function loadSavedReport(entry) {
    try {
      const r = await fetch(`${API_BASE}/analyze/reports/${encodeURIComponent(entry.report_id)}?run_key=${encodeURIComponent(entry.run_key)}&_t=${Date.now()}`);
      if (!r.ok) {
        toast("Report nicht gefunden", "error");
        return;
      }
      const res = await r.json();
      if (res?.markdown) {
        setMarkdown(res.markdown);
        setCurrentSavedId(entry.report_id);
        setError(null);
        toast(`Analyse "${entry.title}" geladen`, "info");
      }
    } catch (e) {
      toast("Fehler beim Laden", "error");
    }
  }

  async function deleteSavedReport(entry) {
    if (!window.confirm(`Analyse "${entry.title}" wirklich löschen?`)) return;
    try {
      await fetch(`${API_BASE}/analyze/reports/${encodeURIComponent(entry.report_id)}?run_key=${encodeURIComponent(entry.run_key)}`, { method: "DELETE" });
      toast("Gelöscht", "info");
      if (currentSavedId === entry.report_id) {
        setMarkdown("");
        setCurrentSavedId(null);
      }
      refreshSavedReports();
    } catch (e) {
      toast("Löschen fehlgeschlagen", "error");
    }
  }

  async function renameSavedReport(entry, newTitle) {
    if (!newTitle || newTitle === entry.title) {
      setRenamingId(null);
      return;
    }
    try {
      await fetch(`${API_BASE}/analyze/reports/${encodeURIComponent(entry.report_id)}?run_key=${encodeURIComponent(entry.run_key)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: newTitle }),
      });
      toast("Umbenannt", "info");
      setRenamingId(null);
      refreshSavedReports();
    } catch (e) {
      toast("Fehler", "error");
    }
  }

  function downloadReport() {
    if (!markdown) return;
    const blob = new Blob([markdown], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `analyze_report_${Date.now()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function resetMcStatus() {
    postApi("/analyze/status/reset", {}).catch(() => {});
    setMcStatus(null);
  }

  // v4-FEAT-C: Run-Compare
  async function runCompare() {
    if (!selectedRun || !compareRun) {
      toast("Bitte zwei Runs auswählen", "error");
      return;
    }
    if (selectedRun.run_type !== compareRun.run_type) {
      toast("Nur Runs vom selben Typ können verglichen werden", "error");
      return;
    }
    setComparing(true);
    setCompareResult(null);
    try {
      const res = await postApi("/analyze/compare", {
        run_file_a: selectedRun.path,
        run_file_b: compareRun.path,
      });
      if (res?.diffs) {
        setCompareResult(res);
        toast("Vergleich fertig", "success");
      } else {
        toast(res?.detail || "Vergleich fehlgeschlagen", "error");
      }
    } catch (e) {
      toast(`Compare Fehler: ${e.message || e}`, "error");
    } finally {
      setComparing(false);
    }
  }

  // v4-FEAT-B: Apply-from-Run
  async function previewApply() {
    if (!selectedRun || !applyStrategyFile) {
      toast("Run und Zieldatei wählen", "error");
      return;
    }
    setApplying(true);
    setApplyPreview(null);
    try {
      const res = await postApi("/analyze/apply-from-run", {
        run_file: selectedRun.path,
        strategy_file: applyStrategyFile,
      });
      if (res?.params) {
        setApplyPreview(res);
      } else {
        toast(res?.detail || "Params-Extraktion fehlgeschlagen", "error");
      }
    } catch (e) {
      toast(`Extract Fehler: ${e.message || e}`, "error");
    } finally {
      setApplying(false);
    }
  }

  async function confirmApply() {
    if (!applyPreview || !applyStrategyFile) return;
    setApplying(true);
    try {
      // Call the existing apply-results endpoint directly
      const res = await postApi(
        `/strategy-files/${encodeURIComponent(applyStrategyFile)}/apply-results`,
        {
          type: "params",
          data: applyPreview.params,
          description: `Apply from ${applyPreview.source} (${applyPreview.n_params} params)`,
        }
      );
      if (res && !res.detail) {
        toast(`${applyPreview.n_params} Parameter auf ${applyStrategyFile} angewandt`, "success");
        setApplyPreview(null);
      } else {
        toast(res?.detail || "Apply fehlgeschlagen", "error");
      }
    } catch (e) {
      toast(`Apply Fehler: ${e.message || e}`, "error");
    } finally {
      setApplying(false);
    }
  }

  // ─── Layout ───────────────────────────────────────
  const ctrlLabel = { fontSize: 10, fontWeight: 700, color: theme.text4, textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4, display: "block" };
  const ctrlInput = {
    width: "100%", padding: "8px 10px", fontSize: 12, fontFamily: fonts.mono,
    border: `1px solid ${theme.border}`, borderRadius: 6, background: theme.white,
    outline: "none", color: theme.text,
  };

  return <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16, alignItems: "start" }}>
    {/* Global CSS fix: prevent card stacking/overlap in the flex-column sidebar.
        Without flex-shrink:0 the cards get squished by the flex parent. */}
    <style>{`.analyze-sidebar-scroll > * { flex-shrink: 0 !important; }`}</style>

    {/* ═══ LEFT: Configuration Panel ═══ */}
    {/* Outer: sticky positioning. Inner: independent scroll container */}
    <div style={{
      position: "sticky",
      top: 16,
      height: "calc(100vh - 32px)",
      minWidth: 0,
    }}>
      <div className="analyze-sidebar-scroll" style={{
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
        paddingRight: 8,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}>

      {/* Run Selector */}
      <Card>
        <CardHeader title="📊 Run auswählen" sub={`${runs.length} verfügbar`}/>
        <div style={{ padding: "12px 16px" }}>
          <label style={ctrlLabel}>Run</label>
          <select
            value={selectedRun?.path || ""}
            onChange={e => {
              const r = runs.find(x => x.path === e.target.value);
              if (r) setSelectedRun(r);
            }}
            style={ctrlInput}
          >
            <option value="">— wählen —</option>
            <optgroup label="Walk-Forward">
              {runs.filter(r => r.run_type === "walkforward").map(r => (
                <option key={r.path} value={r.path}>
                  #{r.run_id} · {r.verdict || "?"} · {r.file.slice(0, 24)}
                </option>
              ))}
            </optgroup>
            <optgroup label="Optimizer">
              {runs.filter(r => r.run_type === "optimizer").map(r => (
                <option key={r.path} value={r.path}>
                  #{r.run_id} · {r.n_trials}t · {r.file.slice(0, 24)}
                </option>
              ))}
            </optgroup>
          </select>

          {selectedRun && <div style={{ marginTop: 10, padding: 10, background: theme.tint, borderRadius: 6, fontSize: 11, lineHeight: 1.6 }}>
            <div><b>Typ:</b> <Pill bg={selectedRun.run_type === "walkforward" ? theme.amberLight : theme.blueLight} color={selectedRun.run_type === "walkforward" ? theme.amberText : theme.blueText}>{selectedRun.run_type}</Pill></div>
            {selectedRun.run_type === "walkforward" && <>
              <div><b>Windows:</b> {selectedRun.windows}/{selectedRun.total_windows}</div>
              <div><b>Verdict:</b> <span style={{fontWeight:700, color:selectedRun.verdict==="ROBUST"?theme.greenText:theme.redText}}>{selectedRun.verdict}</span></div>
              <div><b>OOS:</b> <span style={{fontFamily:fonts.mono}}>${(selectedRun.total_oos_pnl||0).toLocaleString()}</span></div>
            </>}
            {selectedRun.run_type === "optimizer" && <>
              <div><b>Trials:</b> {selectedRun.n_trials}</div>
              <div><b>Best Score:</b> {(selectedRun.best_score||0).toFixed(3)} ({selectedRun.best_grade})</div>
              <div><b>Best PnL:</b> <span style={{fontFamily:fonts.mono, color:(selectedRun.best_pnl||0)>=0?theme.greenText:theme.redText}}>${(selectedRun.best_pnl||0).toLocaleString()}</span></div>
            </>}
          </div>}
        </div>
      </Card>

      {/* Sections */}
      <Card>
        <CardHeader title="📋 Sektionen" sub={`${selectedSections.length}/${availableSections.length} aktiv`}/>
        <div style={{ padding: "10px 16px", maxHeight: 320, overflowY: "auto" }}>
          {availableSections.map(s => (
            <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", cursor: "pointer", fontSize: 12 }}>
              <input type="checkbox" checked={!!sections[s.id]} onChange={e => setSections({...sections, [s.id]: e.target.checked})}/>
              <span style={{ color: sections[s.id] ? theme.text : theme.text3, fontWeight: sections[s.id] ? 600 : 500 }}>{s.label}</span>
            </label>
          ))}
          <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
            <button onClick={() => {
              const all = {}; availableSections.forEach(s => all[s.id] = true); setSections(all);
            }} style={{ fontSize: 10, padding: "4px 8px", border: `1px solid ${theme.border}`, background: theme.white, borderRadius: 5, cursor: "pointer", color: theme.text2 }}>Alle</button>
            <button onClick={() => {
              const none = {}; availableSections.forEach(s => none[s.id] = false); setSections(none);
            }} style={{ fontSize: 10, padding: "4px 8px", border: `1px solid ${theme.border}`, background: theme.white, borderRadius: 5, cursor: "pointer", color: theme.text2 }}>Keine</button>
            <button onClick={() => {
              const def = {}; availableSections.forEach(s => def[s.id] = s.default); setSections(def);
            }} style={{ fontSize: 10, padding: "4px 8px", border: `1px solid ${theme.border}`, background: theme.white, borderRadius: 5, cursor: "pointer", color: theme.text2 }}>Default</button>
          </div>
        </div>
      </Card>

      {/* Advanced Options */}
      <Card>
        <CardHeader title="⚙️ Optionen"/>
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <label style={ctrlLabel}>Filter (optional)</label>
            <input
              value={filterExpr}
              onChange={e => setFilterExpr(e.target.value)}
              placeholder="z.B. grade=A,pnl>50000"
              style={ctrlInput}
            />
            <div style={{ fontSize: 10, color: theme.text4, marginTop: 4 }}>Operatoren: = != &gt; &lt; &gt;= &lt;=</div>
          </div>

          <div>
            <label style={ctrlLabel}>Top-N</label>
            <input
              type="number" min="5" max="100"
              value={topN}
              onChange={e => setTopN(parseInt(e.target.value) || 10)}
              style={{...ctrlInput, width: 100}}
            />
          </div>

          {runType === "walkforward" && (
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={crossValidate} onChange={e => setCrossValidate(e.target.checked)}/>
              <span style={{color: theme.text2}}>Cross-Validation (vs. neuester Optimizer)</span>
            </label>
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={tableOnly} onChange={e => setTableOnly(e.target.checked)}/>
            <span style={{color: theme.text2}}>Nur Tabelle</span>
          </label>

          {runType === "optimizer" && <>
            <div style={{ borderTop: `1px solid ${theme.divider}`, paddingTop: 10, marginTop: 4 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={includeInvalid} onChange={e => setIncludeInvalid(e.target.checked)}/>
                <span style={{color: theme.text2}}>Alle Trials einbeziehen</span>
                <span style={{fontSize: 10, color: theme.text4, marginLeft: 4}}>(inkl. disqualifizierter)</span>
              </label>
              {includeInvalid && (
                <div style={{
                  marginTop: 6, padding: 8, background: theme.amberLight, borderRadius: 6,
                  fontSize: 10.5, color: theme.amberText, lineHeight: 1.5,
                }}>
                  ⚠️ Advanced: Disqualifizierte Trials haben oft Ghost-PnLs (z.B. $180k aus 12 Glücks-Trades).
                  Nutze das nur für Diagnostik — z.B. "welche Parameter führen zu hard_constraints violations".
                </div>
              )}
            </div>
          </>}

          {runType === "optimizer" && <>
            <div style={{ borderTop: `1px solid ${theme.divider}`, paddingTop: 10, marginTop: 4 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={mcEnabled} onChange={e => setMcEnabled(e.target.checked)}/>
                <span style={{color: theme.text2, fontWeight: 600}}>Monte Carlo (Top-N)</span>
              </label>
              {mcEnabled && <>
                <div style={{ marginTop: 8 }}>
                  <label style={ctrlLabel}>MC auf Top</label>
                  <input
                    type="number" min="10" max="500" step="10"
                    value={mcTop}
                    onChange={e => setMcTop(parseInt(e.target.value) || 100)}
                    style={{...ctrlInput, width: 100}}
                  />
                </div>
                <div style={{
                  marginTop: 8, padding: 8, background: theme.amberLight, borderRadius: 6,
                  fontSize: 11, color: theme.amberText, lineHeight: 1.5,
                }}>
                  ⚠️ Läuft Backtest + MC pro Trial. Geschätzt <b>{Math.ceil(mcTop/6)} Min</b> für Top-{mcTop}.
                </div>
              </>}
            </div>
          </>}
        </div>
      </Card>

      {/* v4-FEAT: Optional Report Title */}
      <Card>
        <CardHeader title="🏷️ Analyse-Titel" sub="Optional — wird automatisch vergeben wenn leer"/>
        <div style={{ padding: "12px 16px" }}>
          <input
            type="text"
            value={reportTitle}
            onChange={e => setReportTitle(e.target.value)}
            placeholder="z.B. Baseline ADX<22 oder MC Top-100 (2026-04)"
            maxLength={200}
            style={ctrlInput}
          />
        </div>
      </Card>

      {/* Actions */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Button primary onClick={runAnalysis} disabled={!selectedRun || loading || mcStatus?.running} style={{ width: "100%", padding: "12px 16px", fontSize: 13 }}>
          {loading ? "Läuft..." : mcStatus?.running ? "MC läuft..." : "🔍 Analyse starten"}
        </Button>
        {markdown && <Button onClick={downloadReport} style={{ width: "100%", padding: "10px 16px", fontSize: 12 }}>
          ⬇️ Report als .md herunterladen
        </Button>}
      </div>

      {/* v4-FEAT: Saved Reports */}
      {savedReports.length > 0 && (
        <Card>
          <CardHeader title={`💾 Gespeicherte Analysen`} sub={`${savedReports.length} für diesen Run`}/>
          <div style={{ maxHeight: 340, overflowY: "auto", padding: "6px 0" }}>
            {savedReports.map((r, i) => {
              const isActive = currentSavedId === r.report_id;
              const isRenaming = renamingId === r.report_id;
              const labels = [];
              if (r.mc_top) labels.push(`MC${r.mc_top}`);
              if (r.include_invalid) labels.push("IncInvalid");
              if (r.filter_expr) labels.push("Filtered");
              const ts = r.created_at?.replace(/^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2}).*$/, "$3.$2. $4:$5") || "";
              return (
                <div key={r.report_id}
                     style={{
                       padding: "8px 14px",
                       borderBottom: i < savedReports.length - 1 ? `1px solid ${theme.divider}` : "none",
                       background: isActive ? `rgba(59,130,246,0.08)` : "transparent",
                       borderLeft: isActive ? `3px solid ${theme.blue}` : `3px solid transparent`,
                     }}>
                  {isRenaming ? (
                    <input
                      type="text"
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={() => renameSavedReport(r, renameValue)}
                      onKeyDown={e => {
                        if (e.key === "Enter") renameSavedReport(r, renameValue);
                        if (e.key === "Escape") setRenamingId(null);
                      }}
                      autoFocus
                      style={{ ...ctrlInput, fontSize: 12, padding: "4px 8px" }}
                    />
                  ) : (
                    <>
                      <div style={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 6 }}>
                        <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                             onClick={() => loadSavedReport(r)}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: theme.text,
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {r.title || "Ohne Titel"}
                          </div>
                          <div style={{ fontSize: 10, color: theme.text4, fontFamily: fonts.mono, marginTop: 2 }}>
                            {ts}
                          </div>
                          {labels.length > 0 && (
                            <div style={{ display: "flex", gap: 3, marginTop: 4, flexWrap: "wrap" }}>
                              {labels.map((l, li) => (
                                <span key={li} style={{
                                  fontSize: 9, padding: "1px 6px", borderRadius: 3,
                                  background: theme.blueLight, color: theme.blueText, fontWeight: 700,
                                }}>{l}</span>
                              ))}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                          <button
                            onClick={() => { setRenamingId(r.report_id); setRenameValue(r.title || ""); }}
                            title="Umbenennen"
                            style={{ fontSize: 11, padding: "2px 6px", border: "none", background: "none",
                                     cursor: "pointer", color: theme.text4, borderRadius: 4 }}>
                            ✎
                          </button>
                          <button
                            onClick={() => deleteSavedReport(r)}
                            title="Löschen"
                            style={{ fontSize: 11, padding: "2px 6px", border: "none", background: "none",
                                     cursor: "pointer", color: theme.redText, borderRadius: 4 }}>
                            ✕
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* v4-FEAT-C: Run-Compare */}
      <Card>
        <CardHeader title="⚖️ Run-Compare" sub="Vergleiche mit zweitem Run"/>
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <label style={ctrlLabel}>Vergleichs-Run</label>
          <select
            value={compareRun?.path || ""}
            onChange={e => {
              const r = runs.find(x => x.path === e.target.value);
              setCompareRun(r || null);
              setCompareResult(null);
            }}
            style={ctrlInput}
          >
            <option value="">— wählen —</option>
            {selectedRun && runs.filter(r => r.run_type === selectedRun.run_type && r.path !== selectedRun.path).map(r => (
              <option key={r.path} value={r.path}>
                {r.run_type === "walkforward"
                  ? `#${r.run_id} · ${r.verdict || "?"}`
                  : `#${r.run_id} · ${r.n_trials}t · ${r.best_grade || "?"}`}
              </option>
            ))}
          </select>
          {!selectedRun && <div style={{fontSize:10,color:theme.text4,fontStyle:"italic"}}>Wähle zuerst einen Haupt-Run oben.</div>}
          {selectedRun && <Button onClick={runCompare} disabled={!compareRun || comparing} style={{width:"100%",padding:"8px 12px",fontSize:12}}>
            {comparing ? "Vergleiche..." : "Vergleichen"}
          </Button>}
        </div>
      </Card>

      {/* v4-FEAT-B: Apply from Run */}
      <Card>
        <CardHeader title="📥 Apply from Run" sub="Parameter → YAML"/>
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <label style={ctrlLabel}>Ziel-Strategie-Datei</label>
            <input
              value={applyStrategyFile || ""}
              onChange={e => setApplyStrategyFile(e.target.value)}
              placeholder="z.B. mgc_custom_ibkr.yaml"
              style={ctrlInput}
            />
            <div style={{fontSize:10,color:theme.text4,marginTop:3}}>
              {selectedRun?.run_type === "walkforward"
                ? "Extrahiert WF-Consensus (Median über alle Windows)"
                : "Extrahiert Best-Trial Parameter"}
            </div>
          </div>
          <Button onClick={previewApply} disabled={!selectedRun || !applyStrategyFile || applying} style={{width:"100%",padding:"8px 12px",fontSize:12}}>
            {applying ? "..." : "Vorschau"}
          </Button>
        </div>
      </Card>

      {/* v5-EXPERIMENTS: Create new experiment YAML from current analysis */}
      <Card>
        <CardHeader title="🧪 Experiment erstellen" sub="Neue YAML-Variante ohne Haupt zu ändern"/>
        <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 11, color: theme.text3, lineHeight: 1.5 }}>
            Erstellt eine abgeleitete Test-YAML in <code style={{fontSize:10,background:theme.grayLight,padding:"1px 4px",borderRadius:3}}>strategies/experiments/</code> mit deinen Änderungen.
            Die Haupt-YAML bleibt unangetastet. Nach erfolgreichem WF-Test: promoten.
          </div>
          <Button primary onClick={() => setExpModalOpen(true)}
                  disabled={!selectedRun}
                  style={{width:"100%",padding:"10px 12px",fontSize:12}}>
            ➕ Neues Experiment
          </Button>
        </div>
      </Card>

      {/* v5-EXPERIMENTS: Existing experiments list */}
      {experiments.length > 0 && (
        <Card>
          <CardHeader title="🧪 Experimente" sub={`${experiments.length} vorhanden`}/>
          <div style={{ maxHeight: 320, overflowY: "auto", padding: "4px 0" }}>
            {experiments.map((exp, i) => {
              const statusColors = {
                pending: { bg: theme.grayLight, text: theme.text3 },
                optimized: { bg: theme.blueLight, text: theme.blueText },
                validated: { bg: theme.greenLight, text: theme.greenText },
                promoted: { bg: "rgba(16,185,129,0.15)", text: theme.greenText },
                archived: { bg: theme.grayLight, text: theme.text4 },
              };
              const sc = statusColors[exp.status] || statusColors.pending;
              const ts = exp.created_at?.replace(/T/, " ").slice(5, 16) || "";
              return (
                <div key={exp.filename}
                     style={{
                       padding: "8px 14px",
                       borderBottom: i < experiments.length - 1 ? `1px solid ${theme.divider}` : "none",
                     }}>
                  <div style={{ display: "flex", alignItems: "start", justifyContent: "space-between", gap: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                        <div style={{
                          fontSize: 12, fontWeight: 600, color: theme.text,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          flex: 1, minWidth: 0,
                        }}>
                          {exp.name}
                        </div>
                        <span style={{
                          fontSize: 9, padding: "1px 6px", borderRadius: 3,
                          background: sc.bg, color: sc.text, fontWeight: 700,
                          flexShrink: 0,
                        }}>{exp.status}</span>
                      </div>
                      <div style={{ fontSize: 10, color: theme.text4, fontFamily: fonts.mono }}>
                        {exp.base_yaml} · {ts}
                      </div>
                      {exp.n_changes > 0 && (
                        <div style={{ fontSize: 10, color: theme.text3, marginTop: 2 }}>
                          {exp.n_changes} Änderung{exp.n_changes !== 1 ? "en" : ""}
                          {exp.derived_from_run && ` · von ${exp.derived_from_run}`}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 2, flexShrink: 0 }}>
                      {exp.status !== "promoted" && (
                        <button onClick={() => promoteExperiment(exp.filename)}
                                title="Nach Haupt-YAML promoten"
                                style={{ fontSize:10, padding:"2px 6px", border:"none",
                                         background:"none", cursor:"pointer",
                                         color: theme.greenText, borderRadius:4 }}>
                          ⬆️
                        </button>
                      )}
                      <button onClick={() => deleteExperiment(exp.filename)}
                              title="Löschen"
                              style={{ fontSize:11, padding:"2px 6px", border:"none",
                                       background:"none", cursor:"pointer",
                                       color: theme.redText, borderRadius:4 }}>
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}
      </div>
    </div>

    {/* ═══ RIGHT: Results ═══ */}
    <div style={{ minWidth: 0 }}>

      {/* MC Status Banner */}
      {mcStatus?.running && (
        <Card style={{ marginBottom: 14, background: theme.amberLight, border: `1px solid ${theme.amber}` }}>
          <div style={{ padding: "14px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: theme.amberText }}>
                ⏳ Monte Carlo läuft
                {mcStatus.trial_total > 0 && (
                  <span style={{ fontFamily: fonts.mono, marginLeft: 10, fontWeight: 600 }}>
                    {mcStatus.trial_current || 0} / {mcStatus.trial_total}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, fontFamily: fonts.mono, color: theme.amberText }}>
                {Math.floor((mcStatus.elapsed_seconds||0)/60)}m {(mcStatus.elapsed_seconds||0)%60}s
                {mcStatus.eta_seconds > 0 && (
                  <span style={{ marginLeft: 8, opacity: 0.7 }}>
                    · ETA {Math.floor(mcStatus.eta_seconds/60)}m {mcStatus.eta_seconds%60}s
                  </span>
                )}
              </div>
            </div>

            {/* Progress bar */}
            {mcStatus.trial_total > 0 && (
              <div style={{ marginBottom: 8, height: 6, background: "rgba(255,255,255,0.5)", borderRadius: 3, overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  width: `${Math.min(100, ((mcStatus.trial_current||0) / mcStatus.trial_total) * 100)}%`,
                  background: theme.amber,
                  transition: "width 0.3s ease",
                }}/>
              </div>
            )}

            <div style={{ fontSize: 12, color: theme.amberText }}>{mcStatus.progress}</div>
          </div>
        </Card>
      )}

      {/* MC Error Banner */}
      {mcStatus && !mcStatus.running && mcStatus.error && (
        <Card style={{ marginBottom: 14, background: theme.redLight, border: `1px solid ${theme.red}` }}>
          <div style={{ padding: "14px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.redText }}>❌ MC-Fehler</div>
              <button onClick={resetMcStatus} style={{fontSize:11,padding:"3px 8px",border:`1px solid ${theme.border}`,background:theme.white,borderRadius:5,cursor:"pointer"}}>Reset</button>
            </div>
            <div style={{ fontSize: 12, color: theme.redText, fontFamily: fonts.mono, marginTop: 6 }}>{mcStatus.error}</div>
          </div>
        </Card>
      )}

      {/* Loading */}
      {loading && !mcStatus?.running && (
        <Card><div style={{ padding: 40, textAlign: "center" }}>
          <LoadingState/>
          <div style={{ fontSize: 13, color: theme.text3, marginTop: 8 }}>Analyse läuft...</div>
        </div></Card>
      )}

      {/* Error */}
      {error && (
        <Card style={{ background: theme.redLight, border: `1px solid ${theme.red}` }}>
          <div style={{ padding: "14px 18px" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.redText, marginBottom: 6 }}>Fehler</div>
            <div style={{ fontSize: 12, color: theme.redText, fontFamily: fonts.mono, whiteSpace: "pre-wrap" }}>{error}</div>
          </div>
        </Card>
      )}

      {/* Placeholder / Empty */}
      {!loading && !markdown && !error && !mcStatus?.running && (
        <Card>
          <div style={{ padding: 50, textAlign: "center", color: theme.text3 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🔬</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.text2, marginBottom: 8 }}>Deep-Dive Analyse</div>
            <div style={{ fontSize: 13, lineHeight: 1.6, maxWidth: 460, margin: "0 auto" }}>
              Wähle einen Run links und starte die Analyse. Enthält Parameter-Importance (Random Forest),
              Cluster-Analyse (auto-K via Silhouette), Regime-Segmentierung, Anomalie-Detection und
              automatische Empfehlungen.
            </div>
            {!runs.length && <div style={{fontSize:11,marginTop:16,color:theme.text4}}>Keine Runs gefunden. Bitte zuerst einen Optimizer- oder Walk-Forward-Run durchführen.</div>}
          </div>
        </Card>
      )}

      {/* v4-FEAT-C: Compare Result */}
      {compareResult && (
        <Card style={{ marginBottom: 14 }}>
          <CardHeader
            title="⚖️ Run-Vergleich"
            sub={`Typ: ${compareResult.run_type} · ${compareResult.diffs.length} Parameter`}
            right={<button onClick={() => setCompareResult(null)} style={{fontSize:11,padding:"4px 10px",border:`1px solid ${theme.border}`,background:theme.white,borderRadius:5,cursor:"pointer"}}>Schließen</button>}
          />
          <div style={{ padding: "16px 20px" }}>
            {/* Summary header */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
              {[compareResult.run_a, compareResult.run_b].map((r, idx) => (
                <div key={idx} style={{ padding: 12, background: idx === 0 ? theme.blueLight : theme.purpleLight, borderRadius: 8, fontSize: 12 }}>
                  <div style={{ fontWeight: 700, color: idx === 0 ? theme.blueText : theme.purpleText, marginBottom: 4 }}>
                    {idx === 0 ? "Run A" : "Run B"} · #{r.summary.run_id}
                  </div>
                  <div style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.text3, marginBottom: 6 }}>{r.file}</div>
                  {compareResult.run_type === "walkforward" ? (
                    <>
                      <div>Windows: <b>{r.summary.windows}</b></div>
                      <div>Verdict: <b style={{color: r.summary.robustness==="ROBUST"?theme.greenText:theme.redText}}>{r.summary.robustness || "?"}</b></div>
                      <div style={{fontFamily:fonts.mono}}>OOS: <b style={{color: (r.summary.total_oos_pnl||0)>=0?theme.greenText:theme.redText}}>${(r.summary.total_oos_pnl||0).toLocaleString()}</b></div>
                    </>
                  ) : (
                    <>
                      <div>Trials: <b>{r.summary.n_trials}</b></div>
                      <div>Best Grade: <b>{r.summary.best_grade || "?"}</b></div>
                      <div style={{fontFamily:fonts.mono}}>Score: <b>{(r.summary.best_score || 0).toFixed(3)}</b></div>
                      <div style={{fontFamily:fonts.mono}}>PnL: <b style={{color: (r.summary.best_pnl||0)>=0?theme.greenText:theme.redText}}>${(r.summary.best_pnl||0).toLocaleString()}</b></div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Diff table */}
            <div style={{ borderRadius: 8, border: `1px solid ${theme.border}`, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead style={{ background: theme.tint }}>
                  <tr>
                    <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: theme.text4, textTransform: "uppercase", borderBottom: `1px solid ${theme.border}` }}>Parameter</th>
                    <th style={{ padding: "8px 12px", textAlign: "right", fontSize: 11, fontWeight: 700, color: theme.blueText, textTransform: "uppercase", borderBottom: `1px solid ${theme.border}` }}>Run A</th>
                    <th style={{ padding: "8px 12px", textAlign: "right", fontSize: 11, fontWeight: 700, color: theme.purpleText, textTransform: "uppercase", borderBottom: `1px solid ${theme.border}` }}>Run B</th>
                    <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, fontWeight: 700, color: theme.text4, textTransform: "uppercase", borderBottom: `1px solid ${theme.border}`, width: 80 }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {compareResult.diffs.map(d => (
                    <tr key={d.param} style={{ borderBottom: `1px solid ${theme.divider}`, background: d.same ? "transparent" : "rgba(245,158,11,0.04)" }}>
                      <td style={{ padding: "6px 12px", fontFamily: fonts.mono, fontSize: 11, color: theme.text }}>{d.param}</td>
                      <td style={{ padding: "6px 12px", fontFamily: fonts.mono, fontSize: 11, color: theme.blueText, textAlign: "right" }}>{d.canonical_a != null ? String(d.canonical_a).slice(0, 20) : "—"}</td>
                      <td style={{ padding: "6px 12px", fontFamily: fonts.mono, fontSize: 11, color: theme.purpleText, textAlign: "right" }}>{d.canonical_b != null ? String(d.canonical_b).slice(0, 20) : "—"}</td>
                      <td style={{ padding: "6px 12px", textAlign: "center" }}>
                        {d.same
                          ? <span style={{ color: theme.greenText, fontSize: 14 }}>✓</span>
                          : <span style={{ color: theme.amberText, fontSize: 12, fontWeight: 700 }}>Δ</span>
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{fontSize:11,color:theme.text3,marginTop:10,fontStyle:"italic"}}>
              ✓ = identischer Wert · Δ = Abweichung. Für WF: Werte sind Median über alle Windows.
            </div>
          </div>
        </Card>
      )}

      {/* v4-FEAT-B: Apply Preview */}
      {applyPreview && (
        <Card style={{ marginBottom: 14, border: `2px solid ${theme.blue}` }}>
          <CardHeader
            title="📥 Apply Vorschau"
            sub={`${applyPreview.source} → ${applyPreview.strategy_file}`}
            right={
              <div style={{display:"flex",gap:6}}>
                <Button primary onClick={confirmApply} disabled={applying} style={{fontSize:12,padding:"6px 14px"}}>
                  {applying ? "..." : `✓ ${applyPreview.n_params} Parameter anwenden`}
                </Button>
                <button onClick={()=>setApplyPreview(null)} style={{fontSize:11,padding:"6px 12px",border:`1px solid ${theme.border}`,background:theme.white,borderRadius:6,cursor:"pointer"}}>
                  Abbrechen
                </button>
              </div>
            }
          />
          <div style={{ padding: "12px 20px" }}>
            <div style={{fontSize:12,color:theme.text2,marginBottom:10}}>
              Diese {applyPreview.n_params} Parameter werden in <code style={{padding:"1px 6px",background:theme.tint,borderRadius:4,fontFamily:fonts.mono,fontSize:11}}>{applyPreview.strategy_file}</code> geschrieben:
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 6 }}>
              {Object.entries(applyPreview.params||{}).map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", borderRadius: 5, background: theme.tint, fontSize: 11 }}>
                  <span style={{ color: theme.text3 }}>{k}</span>
                  <span style={{ fontFamily: fonts.mono, fontWeight: 700, color: theme.text }}>
                    {typeof v === "number" ? v.toFixed(3) : String(v)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Markdown Result */}
      {markdown && !loading && (
        <Card>
          <div style={{ padding: "16px 24px 24px" }}>
            <MarkdownView markdown={markdown}/>
          </div>
        </Card>
      )}

    </div>

    {/* v5-EXPERIMENTS: Create Modal */}
    {expModalOpen && (
      <div style={{
        position: "fixed", inset: 0, background: "rgba(15,23,41,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: 20,
      }} onClick={() => setExpModalOpen(false)}>
        <div style={{
          background: theme.white, borderRadius: 12, maxWidth: 720, width: "100%",
          maxHeight: "90vh", overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: "20px 24px", borderBottom: `1px solid ${theme.divider}` }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>🧪 Neues Experiment erstellen</div>
            <div style={{ fontSize: 12, color: theme.text3, marginTop: 4 }}>
              Erstellt eine abgeleitete YAML in <code style={{background:theme.grayLight,padding:"1px 5px",borderRadius:3}}>strategies/experiments/</code> — die Haupt-YAML bleibt unangetastet.
            </div>
          </div>

          <div style={{ padding: "18px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Base YAML */}
            <div>
              <label style={ctrlLabel}>Base-YAML (Quelle)</label>
              <select value={expBaseYaml} onChange={e => setExpBaseYaml(e.target.value)} style={ctrlInput}>
                <option value="">— wählen —</option>
                {strategyFiles.map(f => (
                  <option key={f.filename} value={f.filename}>
                    {f.filename} · {f.instrument} · {f.status}
                  </option>
                ))}
              </select>
              <div style={{fontSize:10,color:theme.text4,marginTop:3}}>
                Die Datei wird kopiert, deine Änderungen werden appliziert.
              </div>
            </div>

            {/* Name */}
            <div>
              <label style={ctrlLabel}>Experiment-Name</label>
              <input value={expName} onChange={e => setExpName(e.target.value)}
                     placeholder="z.B. adx_expand oder bool_fixed"
                     maxLength={60}
                     style={ctrlInput}/>
              <div style={{fontSize:10,color:theme.text4,marginTop:3}}>
                Dateiname wird: <code>{Object.keys(strategyFiles.length ? {x:1} : {}).length >= 0 ? (expBaseYaml.replace(/\.yaml$/,"")) : "[base]"}__{(expName || "[name]").toLowerCase().replace(/[^\w-]+/g,"_")}.yaml</code>
              </div>
            </div>

            {/* Note */}
            <div>
              <label style={ctrlLabel}>Notiz (optional)</label>
              <input value={expNote} onChange={e => setExpNote(e.target.value)}
                     placeholder="z.B. Testing wider ADX range based on run #36 analysis"
                     maxLength={200}
                     style={ctrlInput}/>
            </div>

            {/* v6-SMART-BUILDER: Changes editor with schema-driven form */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
                <label style={ctrlLabel}>Änderungen ({expChanges.length})</label>
                <button
                  onClick={() => setExpExpertMode(!expExpertMode)}
                  style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4,
                    border: `1px solid ${theme.border}`, background: theme.white,
                    color: theme.text3, cursor: "pointer" }}
                  title="Wechsle zwischen Smart-Form und Pfad-Editor">
                  {expExpertMode ? "📋 Smart Builder" : "⚙️ Expert Mode"}
                </button>
              </div>

              {expExpertMode ? (
                /* ── EXPERT MODE: manual path editor (legacy) ── */
                <>
                  <div style={{fontSize:11,color:theme.text3,marginBottom:8,lineHeight:1.5}}>
                    Direkter Pfad-Editor. <strong>Path</strong> dotted notation (z.B. <code>goals.ranges.adx_threshold</code>).
                    <strong> Value</strong> in YAML-Syntax.
                  </div>
                  {expChanges.map((ch, idx) => (
                    <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 28px",
                      gap: 6, marginBottom: 6, alignItems: "start" }}>
                      <input value={ch.path}
                        onChange={e => {
                          const copy = [...expChanges];
                          copy[idx] = { ...copy[idx], path: e.target.value };
                          setExpChanges(copy);
                        }}
                        placeholder="z.B. goals.ranges.adx_threshold"
                        style={{...ctrlInput, fontSize:11, fontFamily:fonts.mono}}/>
                      <input value={ch.rawValue !== undefined ? ch.rawValue : JSON.stringify(ch.value)}
                        onChange={e => {
                          const copy = [...expChanges];
                          const raw = e.target.value;
                          let parsed = raw;
                          try { parsed = JSON.parse(raw); }
                          catch {
                            if (raw === "true") parsed = true;
                            else if (raw === "false") parsed = false;
                            else if (raw === "null" || raw === "") parsed = null;
                            else if (!isNaN(parseFloat(raw)) && isFinite(raw)) parsed = parseFloat(raw);
                          }
                          copy[idx] = { ...copy[idx], rawValue: raw, value: parsed };
                          setExpChanges(copy);
                        }}
                        placeholder="[22,24,26,28]"
                        style={{...ctrlInput, fontSize:11, fontFamily:fonts.mono}}/>
                      <button onClick={() => setExpChanges(expChanges.filter((_, i) => i !== idx))}
                        title="Entfernen"
                        style={{ border: "none", background: "none", cursor: "pointer",
                          color: theme.redText, fontSize: 14, padding: 4 }}>✕</button>
                    </div>
                  ))}
                  <button
                    onClick={() => setExpChanges([...expChanges, {path:"", value:null, rawValue:"", description:""}])}
                    style={{ marginTop: 6, fontSize: 12, padding: "6px 12px", borderRadius: 6,
                      border: `1px dashed ${theme.border}`, background: theme.white,
                      color: theme.text3, cursor: "pointer", width: "100%" }}>
                    + Änderung hinzufügen
                  </button>
                </>
              ) : (
                /* ── SMART BUILDER: schema-driven form ── */
                <>
                  {expSchemaLoading && (
                    <div style={{padding:"14px 16px",fontSize:11,color:theme.text3,textAlign:"center"}}>
                      Lade Schema von <code>{expBaseYaml}</code>...
                    </div>
                  )}
                  {!expSchemaLoading && !expSchema && expBaseYaml && (
                    <div style={{padding:"14px 16px",fontSize:11,color:theme.amberText,background:theme.amberLight,borderRadius:6}}>
                      ⚠️ Schema konnte nicht geladen werden. Wechsle zu Expert Mode für manuelles Editieren.
                    </div>
                  )}
                  {!expSchemaLoading && !expBaseYaml && (
                    <div style={{padding:"14px 16px",fontSize:11,color:theme.text4,textAlign:"center"}}>
                      Wähle zuerst eine Base-YAML ↑
                    </div>
                  )}
                  {expSchema?.categories?.map(cat => {
                    const open = expOpenSections[cat.key] ?? false;
                    const editsInCat = cat.fields.filter(f => expFieldEdits[f.path]?.enabled).length;
                    return (
                      <div key={cat.key} style={{ border: `1px solid ${theme.divider}`, borderRadius: 6, marginBottom: 8, overflow: "hidden" }}>
                        {/* Category header */}
                        <button
                          onClick={() => setExpOpenSections({...expOpenSections, [cat.key]: !open})}
                          style={{ width: "100%", border: "none", background: open ? theme.tint : theme.white,
                            padding: "10px 12px", cursor: "pointer", textAlign: "left",
                            display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 14 }}>{cat.icon}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>{cat.label}</span>
                            <span style={{ fontSize: 10, color: theme.text4 }}>({cat.fields.length} Felder{editsInCat > 0 ? ` · ${editsInCat} geändert` : ""})</span>
                          </div>
                          <span style={{ fontSize: 10, color: theme.text4 }}>{open ? "▼" : "▶"}</span>
                        </button>

                        {open && (
                          <div style={{ padding: "8px 12px", background: theme.white }}>
                            <div style={{ fontSize: 10, color: theme.text4, marginBottom: 8, fontStyle: "italic" }}>{cat.hint}</div>
                            {cat.fields.map(field => {
                              const edit = expFieldEdits[field.path] || { enabled: false, newValue: field.current_value };
                              const toggleEnabled = () => {
                                setExpFieldEdits({
                                  ...expFieldEdits,
                                  [field.path]: { enabled: !edit.enabled, newValue: edit.newValue ?? field.current_value }
                                });
                              };
                              const updateValue = (v) => {
                                setExpFieldEdits({
                                  ...expFieldEdits,
                                  [field.path]: { enabled: true, newValue: v }
                                });
                              };
                              return (
                                <div key={field.path} style={{
                                  padding: "8px 10px", marginBottom: 4,
                                  background: edit.enabled ? theme.amberLight : "transparent",
                                  borderRadius: 4, border: edit.enabled ? `1px solid ${theme.amber || "#f59e0b"}` : `1px solid transparent`,
                                }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                                    <input type="checkbox" checked={edit.enabled} onChange={toggleEnabled} style={{ cursor: "pointer" }}/>
                                    <span style={{ fontSize: 11, fontWeight: 600, color: theme.text, flex: 1 }}>{field.label}</span>
                                    <code style={{ fontSize: 9, color: theme.text4, fontFamily: fonts.mono }}>{field.path.split(".").pop()}</code>
                                  </div>
                                  {field.hint && <div style={{ fontSize: 10, color: theme.text4, marginLeft: 22, marginBottom: 4 }}>{field.hint}</div>}

                                  <div style={{ marginLeft: 22, display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                                    <span style={{ color: theme.text4, minWidth: 50 }}>Aktuell:</span>
                                    <code style={{ fontSize: 10, fontFamily: fonts.mono, color: theme.text3 }}>
                                      {field.type === "range_triplet"
                                        ? `${field.current_value?.min} → ${field.current_value?.max} (step ${field.current_value?.step})`
                                        : JSON.stringify(field.current_value)}
                                    </code>
                                  </div>

                                  {edit.enabled && (
                                    <div style={{ marginLeft: 22, marginTop: 6 }}>
                                      {/* RENDER WIDGET BY TYPE */}
                                      {field.widget === "number" && (
                                        <input type="number" step="any"
                                          value={edit.newValue ?? ""}
                                          onChange={e => updateValue(e.target.value === "" ? null : Number(e.target.value))}
                                          style={{...ctrlInput, fontSize:11, width: 140}}/>
                                      )}
                                      {field.widget === "toggle" && (
                                        <div style={{ display: "flex", gap: 6 }}>
                                          <button onClick={() => updateValue(true)}
                                            style={{ padding: "4px 10px", fontSize: 11,
                                              background: edit.newValue === true ? theme.greenBright : theme.white,
                                              color: edit.newValue === true ? "#fff" : theme.text,
                                              border: `1px solid ${theme.border}`, borderRadius: 4, cursor: "pointer" }}>true</button>
                                          <button onClick={() => updateValue(false)}
                                            style={{ padding: "4px 10px", fontSize: 11,
                                              background: edit.newValue === false ? theme.redText : theme.white,
                                              color: edit.newValue === false ? "#fff" : theme.text,
                                              border: `1px solid ${theme.border}`, borderRadius: 4, cursor: "pointer" }}>false</button>
                                        </div>
                                      )}
                                      {field.widget === "range" && field.type === "range_triplet" && (
                                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                          <span style={{ fontSize: 10, color: theme.text4 }}>Min</span>
                                          <input type="number" step="any"
                                            value={edit.newValue?.min ?? field.current_value?.min ?? ""}
                                            onChange={e => updateValue({ ...edit.newValue, min: e.target.value === "" ? null : Number(e.target.value) })}
                                            style={{...ctrlInput, fontSize:11, width: 70}}/>
                                          <span style={{ fontSize: 10, color: theme.text4 }}>Max</span>
                                          <input type="number" step="any"
                                            value={edit.newValue?.max ?? field.current_value?.max ?? ""}
                                            onChange={e => updateValue({ ...edit.newValue, max: e.target.value === "" ? null : Number(e.target.value) })}
                                            style={{...ctrlInput, fontSize:11, width: 70}}/>
                                          <span style={{ fontSize: 10, color: theme.text4 }}>Step</span>
                                          <input type="number" step="any"
                                            value={edit.newValue?.step ?? field.current_value?.step ?? ""}
                                            onChange={e => updateValue({ ...edit.newValue, step: e.target.value === "" ? null : Number(e.target.value) })}
                                            style={{...ctrlInput, fontSize:11, width: 70}}/>
                                        </div>
                                      )}
                                      {(field.widget === "multi_choice_numeric" || field.widget === "multi_choice_string") && (
                                        <SmartMultiChoice
                                          currentValue={field.current_value}
                                          newValue={edit.newValue}
                                          numeric={field.widget === "multi_choice_numeric"}
                                          onChange={updateValue}
                                          theme={theme} fonts={fonts}
                                        />
                                      )}
                                      {field.widget === "text" && (
                                        <input type="text"
                                          value={typeof edit.newValue === "string" ? edit.newValue : JSON.stringify(edit.newValue)}
                                          onChange={e => updateValue(e.target.value)}
                                          style={{...ctrlInput, fontSize:11}}/>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          <div style={{
            padding: "14px 24px", borderTop: `1px solid ${theme.divider}`,
            display: "flex", gap: 10, justifyContent: "flex-end",
          }}>
            <Button onClick={() => setExpModalOpen(false)}
                    style={{padding:"8px 16px",fontSize:12}}>
              Abbrechen
            </Button>
            <Button primary onClick={createExperiment}
                    disabled={!expBaseYaml || !expName || expCreating}
                    style={{padding:"8px 20px",fontSize:12}}>
              {expCreating ? "Erstelle..." : "🧪 Experiment erstellen"}
            </Button>
          </div>
        </div>
      </div>
    )}

  </div>;
}
